import { Component, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import { useMenu } from '../contexts/MenuContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { isSuperAdmin } from '../utils/auth';
import { endSession } from '../utils/session';
// Menü yolu çözümlemesi (menuPathFallbacks) artık ortak: sidebar + Favorilerim yıldızı.
import { menuPathFallbacks } from '../utils/menuResolve';
import { StarIcon } from './ui/FavoriteStar';
import salesOpsMark from '../assets/salesops-mark.png';
import salesOpsLogo from '../assets/salesops-logo.png';
import './NavMenu.css';

// Marka bloğu iki biçimli: açık menüde SalesOps wordmark'ı (login ekranıyla aynı marka),
// daraltılmış menüde ise kare SalesOps amblemi — geniş wordmark 40px'lik kareye sığmaz.
// Seçim CSS'te (data-sidebar-collapsed) yapılır; JS'te ternary olsaydı oto-gizlenen menüde
// hover'la her açılış/kapanışta görsel değişir, titreme olurdu.
const collapsedLogoPath = salesOpsMark;
const expandedLogoPath = salesOpsLogo;

// Finans alt yapısı: Alınan Tahsilatlar & Ödemelerim (TAHSILAT_ODEME.md §1).
// Tek kaynak — hem defaultMenus'a hem DB-menüsüne enjeksiyona kullanılır (migration uygulanmasa da görünür).
// Sıra §1 ile birebir: Nakit · Havale/EFT · Kredi Kartı · Çek · Senet · DBS · Manuel Giriş.
// Sol menü SADE/NÖTRDÜR: renkli tür rozeti YALNIZ içerik alanında kullanılır (§8).
const financeMenuItems = [
  { id: 9730, parentID: 9806, name: 'FinanceCollections', title: 'Alınan Tahsilatlar', hasChild: true, sortOrder: 5 },
  { id: 9731, parentID: 9730, name: 'CollectionCash', title: 'Nakit', path: '/finance/collections/cash', sortOrder: 1 },
  { id: 9738, parentID: 9730, name: 'CollectionBankTransfer', title: 'Havale/EFT', path: '/finance/collections/bank-transfer', sortOrder: 2 },
  { id: 9732, parentID: 9730, name: 'CollectionCreditCard', title: 'Kredi Kartı', path: '/finance/collections/credit-card', sortOrder: 3 },
  { id: 9733, parentID: 9730, name: 'CollectionCheque', title: 'Çek', path: '/finance/collections/cheque', sortOrder: 4 },
  { id: 9734, parentID: 9730, name: 'CollectionNote', title: 'Senet', path: '/finance/collections/promissory-note', sortOrder: 5 },
  { id: 9735, parentID: 9730, name: 'CollectionDbs', title: 'DBS', path: '/finance/collections/dbs', sortOrder: 6 },
  // #827 — Borç Girişi (9736) ile Alacak Girişi (9746) YER DEĞİŞTİRDİ. Id'ler korunur (RoleRight/
  // UserRight onlara bağlı); değişen parent + path. Gerekçe yön: tahsilat grubunun her kalemi
  // müşterinin borcunu AZALTIR, ödeme grubunun her kalemi ARTIRIR — eski yerleşim tersti.
  { id: 9746, parentID: 9730, name: 'CollectionCreditEntry', title: 'Alacak Girişi', path: '/finance/collections/credit-entry', sortOrder: 7 },
  // #Faz2 Tahsilat–Fatura Eşleştirme (migration 20260713101000_AddFinanceAllocationMenus ile birebir).
  { id: 9737, parentID: 9730, name: 'UnallocatedCollections', title: 'Dağıtılmamış Tahsilatlar', path: '/finance/unallocated', sortOrder: 8 },
  { id: 9750, parentID: 9806, name: 'InvoiceAging', title: 'Yaşlandırma', path: '/finance/aging', sortOrder: 7 },
  // Ödeme Tipleri: ekran vardı ama menüde HİÇ kaydı yoktu, yalnız URL ile açılabiliyordu
  // (migration 20260731140000_AddPaymentTypesMenu). Tahsilat/ödeme ve fatura ödeme bilgisinin
  // ortak tanımı olduğu için Finans altında.
  { id: 9751, parentID: 9806, name: 'PaymentTypes', title: 'Ödeme Tipleri', path: '/payment-types', sortOrder: 8 },
  { id: 9740, parentID: 9806, name: 'FinancePayments', title: 'Ödemelerim', hasChild: true, sortOrder: 6 },
  { id: 9741, parentID: 9740, name: 'PaymentCash', title: 'Nakit', path: '/finance/payments/cash', sortOrder: 1 },
  { id: 9747, parentID: 9740, name: 'PaymentBankTransfer', title: 'Havale/EFT', path: '/finance/payments/bank-transfer', sortOrder: 2 },
  { id: 9742, parentID: 9740, name: 'PaymentCreditCard', title: 'Kredi Kartı', path: '/finance/payments/credit-card', sortOrder: 3 },
  { id: 9743, parentID: 9740, name: 'PaymentCheque', title: 'Çek', path: '/finance/payments/cheque', sortOrder: 4 },
  { id: 9744, parentID: 9740, name: 'PaymentNote', title: 'Senet', path: '/finance/payments/promissory-note', sortOrder: 5 },
  { id: 9745, parentID: 9740, name: 'PaymentDbs', title: 'DBS', path: '/finance/payments/dbs', sortOrder: 6 },
  // #827 — bkz. yukarıdaki not: Borç Girişi Alınan Tahsilatlar'dan buraya geldi.
  { id: 9736, parentID: 9740, name: 'PaymentDebitEntry', title: 'Borç Girişi', path: '/finance/payments/debit-entry', sortOrder: 7 },
];

/**
 * #827/#829/#833-835 — YERİ DEĞİŞEN menüler.
 *
 * Bu satırların Id'si KORUNUR (RoleRight/UserRight onlara bağlı; Id değişse yetkiler düşerdi),
 * değişen yalnız ebeveyn / başlık / path / grup olup olmadığıdır. Migration DB'yi günceller, ama
 * migration uygulanmamış ya da eski konumuyla yetkilendirilmiş bir ortamda DB satırı BAYAT gelir —
 * ve bayat satır enjeksiyon kontrolüne (`has`) takılmadığı için menü İKİ KEZ çizilirdi.
 *
 * Çözüm REPORT_MENU'nün `reportShape` deseninin aynısı: DB'den ne gelirse gelsin bu satırların
 * konumu Id üzerinden buradaki değerlere çekilir. Satırın DB'den gelmiş olması (yani YETKİSİ)
 * korunur; yalnız yeri düzeltilir.
 */
const RELOCATED_MENUS = new Map([
  // #827 Borç Girişi ⇄ Alacak Girişi
  [9746, { parentID: 9730, name: 'CollectionCreditEntry', title: 'Alacak Girişi', path: '/finance/collections/credit-entry', sortOrder: 7, hasChild: false }],
  [9736, { parentID: 9740, name: 'PaymentDebitEntry', title: 'Borç Girişi', path: '/finance/payments/debit-entry', sortOrder: 7, hasChild: false }],
  // #829 Konsinye İrsaliye → Belge Merkezi › Konsinye, adı "Konsinye Satış İrsaliyesi"
  [9138, { parentID: 9571, name: 'ConsignmentWaybills', title: 'Konsinye Satış İrsaliyesi', path: '/consignment-waybills', sortOrder: 1, hasChild: false }],
  // #833-835 Üst başlıklar yapraktan GRUBA döndü — path'leri kalkar.
  [9526, { parentID: 9522, name: 'ReturnOrders', title: 'Siparişler', path: null, sortOrder: 1, hasChild: true }],
  [9535, { parentID: 9522, name: 'ReturnWaybills', title: 'İrsaliyeler', path: null, sortOrder: 2, hasChild: true }],
  [9524, { parentID: 9522, name: 'ReturnInvoices', title: 'Faturalar', path: null, sortOrder: 3, hasChild: true }],
]);

// Gamification (Satış Maratonu) — tek kaynak; hem defaultMenus'a hem DB-menüsüne enjekte edilir.
// Üst grup (9814) + Satış Maratonu liderlik tablosu (9815) + Rozetler kataloğu (9816).
const gamificationMenuItems = [
  { id: 9815, parentID: 9814, name: 'Leaderboard', title: 'Satış Maratonu', path: '/leaderboard', sortOrder: 1 },
  { id: 9816, parentID: 9814, name: 'GamificationBadges', title: 'Rozetler & Başarımlar', path: '/gamification/badges', sortOrder: 2 },
];

// Raporlar (9812) ağacı — TEK kaynak; hem defaultMenus'a hem DB-menüsü fallback'ine yayılır.
// DB karşılığı: 20260728180000_RestructureFixedReportsMenu. Yapı Melih Bey'in tasarımıdır:
// tüm rapor grupları Sabit Raporlar (9880) altında toplanır.
// path'i OLMAYAN maddeler = raporu henüz yazılmadı → pasif + "Yakında" (menuCaptionFallbacks).
const REPORT_MENU = [
  { id: 9880, parentID: 9812, name: 'GrpReportFixed', title: 'Sabit Raporlar', hasChild: true, sortOrder: 1 },

  // Tanımlamalar
  { id: 9860, parentID: 9880, name: 'GrpReportTemplate', title: 'Tanımlamalar', hasChild: true, sortOrder: 1 },
  { id: 9930, parentID: 9860, name: 'ProductListReport', title: 'Ürün Listesi', path: '/reports/product-list', sortOrder: 1 },
  { id: 9822, parentID: 9860, name: 'TemplateCustomer', title: 'Müşteri Listesi', path: '/reports/template-customer', sortOrder: 2 },
  { id: 9882, parentID: 9860, name: 'RoutePlanDetailReport', title: 'Rut Listesi', path: '/reports/route-plans', sortOrder: 3 },
  { id: 9881, parentID: 9860, name: 'STRoutePlanList', title: 'ST – Rut Listesi', path: '/reports/st-route-plans', sortOrder: 4 },
  { id: 9931, parentID: 9860, name: 'DistChannelListReport', title: 'Dağıtım Kanalı Listesi', path: '/reports/dist-channels', sortOrder: 5 },
  { id: 9932, parentID: 9860, name: 'VehicleListReport', title: 'Araç Tanım Listesi', path: '/reports/vehicle-list', sortOrder: 6 },
  { id: 9933, parentID: 9860, name: 'DistributorListReport', title: 'Distribütör Listesi', path: '/reports/distributor-list', sortOrder: 7 },
  { id: 9904, parentID: 9860, name: 'WarehouseListReport', title: 'Depo Tanım Listesi', path: '/reports/warehouse-list', sortOrder: 8 },
  { id: 9905, parentID: 9860, name: 'RepresentativeListReport', title: 'Satış Temsilcisi Listesi', path: '/reports/representative-list', sortOrder: 9 },
  { id: 9906, parentID: 9860, name: 'PriceListReport', title: 'Fiyat Listesi', path: '/reports/price-list', sortOrder: 10 },
  { id: 9907, parentID: 9860, name: 'CampaignListReport', title: 'Kampanya Listesi', path: '/reports/campaign-list', sortOrder: 11 },

  // Belge Kayıtları
  { id: 9830, parentID: 9880, name: 'GrpReportDocument', title: 'Belge Kayıtları', hasChild: true, sortOrder: 2 },
  { id: 9831, parentID: 9830, name: 'DocumentOrderList', title: 'Sipariş Listesi', path: '/reports/order-list', sortOrder: 1 },
  { id: 9908, parentID: 9830, name: 'WaybillListReport', title: 'İrsaliye Listesi', path: '/reports/waybill-list', sortOrder: 2 },
  { id: 9823, parentID: 9830, name: 'TemplateInvoice', title: 'Fatura Listesi', path: '/reports/invoice-list', sortOrder: 3 },
  { id: 9909, parentID: 9830, name: 'StockInReport', title: 'Stok Giriş', path: '/reports/stock-entry', sortOrder: 4 },
  { id: 9910, parentID: 9830, name: 'StockOutReport', title: 'Stok Çıkış', path: '/reports/stock-exit', sortOrder: 5 },
  { id: 9911, parentID: 9830, name: 'StockTransferReport', title: 'Stok Transferi', path: '/reports/stock-transfer', sortOrder: 6 },

  // Finans (Tahsilat ve Ödeme) — tamamı yeni
  { id: 9850, parentID: 9880, name: 'GrpReportFinance', title: 'Finans (Tahsilat ve Ödeme)', hasChild: true, sortOrder: 3 },
  { id: 9912, parentID: 9850, name: 'CashCollectionReport', title: 'Nakit', path: '/reports/finance-cash', sortOrder: 1 },
  { id: 9913, parentID: 9850, name: 'TransferCollectionReport', title: 'Havale/EFT', path: '/reports/finance-transfer', sortOrder: 2 },
  { id: 9914, parentID: 9850, name: 'CreditCardCollectionReport', title: 'Kredi Kartı', path: '/reports/finance-card', sortOrder: 3 },
  { id: 9915, parentID: 9850, name: 'ChequeReport', title: 'Çek', path: '/reports/finance-cheque', sortOrder: 4 },
  { id: 9916, parentID: 9850, name: 'PromissoryNoteReport', title: 'Senet', path: '/reports/finance-note', sortOrder: 5 },
  { id: 9917, parentID: 9850, name: 'DbsReport', title: 'DBS', path: '/reports/finance-dbs', sortOrder: 6 },
  { id: 9918, parentID: 9850, name: 'BalanceReport', title: 'Borç – Alacak', path: '/reports/finance-manual', sortOrder: 7 },

  // Ziyaret
  { id: 9890, parentID: 9880, name: 'GrpReportVisit', title: 'Ziyaret', hasChild: true, sortOrder: 4 },
  { id: 9832, parentID: 9890, name: 'DocumentVisitAnalysis', title: 'Ziyaret Analizi', path: '/reports/visit-analysis-criteria', sortOrder: 1 },
  { id: 9885, parentID: 9890, name: 'VisitDetailReport', title: 'Ziyaret Detay Raporu', path: '/reports/visit-detail', sortOrder: 2 },
  { id: 9118, parentID: 9890, name: 'FieldVisits', title: 'Saha Ziyaretleri', path: '/reports/field-visits', sortOrder: 3 },

  // Hareket Kayıtları
  { id: 9891, parentID: 9880, name: 'GrpReportMovement', title: 'Hareket Kayıtları', hasChild: true, sortOrder: 5 },
  { id: 9919, parentID: 9891, name: 'StockLedgerSummaryReport', title: 'Stok Ekstresi Özet', path: '/reports/stock-ledger-summary', sortOrder: 1 },
  { id: 9884, parentID: 9891, name: 'StockLedgerReport', title: 'Stok Ekstresi Detay', path: '/reports/stock-ledger', sortOrder: 2 },
  { id: 9883, parentID: 9891, name: 'AssetStatusReport', title: 'Demirbaş Son Durum', path: '/reports/asset-status', sortOrder: 3 },
  { id: 9920, parentID: 9891, name: 'CustomerStatementSummaryReport', title: 'Müşteri Hesap Ekstresi Özet', path: '/reports/customer-statement-summary', sortOrder: 4 },
  { id: 9014, parentID: 9891, name: 'AccountStatement', title: 'Müşteri Hesap Ekstresi Detay', path: '/reports/customer-statement-detail', sortOrder: 5 },

  // Analizler
  { id: 9840, parentID: 9880, name: 'GrpReportAnalysis', title: 'Analizler', hasChild: true, sortOrder: 6 },
  { id: 9111, parentID: 9840, name: 'SalesAnalytics', title: 'Satış Analizi', path: '/sales-analytics', sortOrder: 1 },
  { id: 9113, parentID: 9840, name: 'SalesDetailReport', title: 'Satış Detay Raporu', path: '/reports/sales-detail', sortOrder: 2 },
  { id: 9921, parentID: 9840, name: 'CustomerProductMovementReport', title: 'Müşteri – Ürün Hareket Analizi', path: '/reports/customer-product', sortOrder: 3 },

  // Özet Dashboard
  { id: 9892, parentID: 9880, name: 'GrpReportDashboard', title: 'Özet Dashboard', hasChild: true, sortOrder: 7 },
  { id: 9109, parentID: 9892, name: 'Reports', title: 'Gösterge', path: '/reports', sortOrder: 1 },
  { id: 9121, parentID: 9892, name: 'VisitTracking', title: 'Ziyaret Takip', path: '/visit-tracking', sortOrder: 2 },

  // Eski Raporlar — yeni tasarımda yeri olmayan mevcut raporlar
  { id: 9893, parentID: 9880, name: 'GrpReportLegacy', title: 'Eski Raporlar', hasChild: true, sortOrder: 8 },
  { id: 9886, parentID: 9893, name: 'CurrentStockReport', title: 'Güncel Stok Raporu', path: '/reports/current-stock', sortOrder: 1 },
  { id: 9114, parentID: 9893, name: 'CollectionDetailReport', title: 'Tahsilat Detay Raporu', path: '/reports/collection-detail', sortOrder: 2 },
  { id: 9093, parentID: 9893, name: 'Visits', title: 'Ziyaretler', path: '/visits', sortOrder: 3 },
];

// Raporu henüz yazılmamış menülerin altına düşen küçük etiket. Menu tablosunda Caption
// kolonu YOK; bu yüzden etiket isim üzerinden burada eşlenir.
const menuCaptionFallbacks = Object.fromEntries(
  REPORT_MENU.filter((m) => !m.path && !m.hasChild).map((m) => [m.name, 'Yakında']),
);

// name → { parentID, title, sortOrder }: DB'den eski konumuyla gelen rapor satırlarını
// yeni yapıya hizalamak için (bkz. sourceMenus).
const reportShape = new Map(
  REPORT_MENU.map((m) => [m.name, { parentID: m.parentID, title: m.title, sortOrder: m.sortOrder }]),
);

const defaultMenus = [
  // --- Dashboards (Rona AI) — migration 20260713170000_AddDashboardsMenuGroup ile DB'ye de eklenir.
  // SortOrder 0: login sonrası ilk bakılan yer olduğu için listenin en üstünde durur.
  { id: 9870, name: 'GrpDashboards', title: 'Dashboards', hasChild: true, sortOrder: 0 },
  { id: 9871, parentID: 9870, name: 'DashboardGeneral', title: 'Genel', path: '/dashboards/general', sortOrder: 1 },
  { id: 9872, parentID: 9870, name: 'DashboardCustomer', title: 'Müşteri 360', path: '/dashboards/customer', sortOrder: 2 },
  { id: 9873, parentID: 9870, name: 'DashboardProduct', title: 'Ürün & SKU', path: '/dashboards/product', sortOrder: 3 },
  { id: 9874, parentID: 9870, name: 'DashboardFinance', title: 'Finans', path: '/dashboards/finance', sortOrder: 4 },
  { id: 9875, parentID: 9870, name: 'DashboardField', title: 'Saha & Temsilci', path: '/dashboards/field', sortOrder: 5 },

  // --- 12 ana menü (üst grup) ---
  { id: 9801, name: 'GrpDistribution', title: 'Dağıtım Kanalı', hasChild: true, sortOrder: 1 },
  // #781 Satış Organizasyonu (9813) KALDIRILDI — son iki ekranı Saha Yönetimi'ne taşındı ve grup
  // boşaldı (bkz. 20260726100000_MoveSalesOrgMenusToFieldManagement). DB'de pasifleştirilir.
  { id: 9824, name: 'GrpFieldManagement', title: 'Saha Yönetimi', hasChild: true, sortOrder: 2 },
  { id: 9802, name: 'GrpCustomer', title: 'Müşteri 360', hasChild: true, sortOrder: 3 },
  { id: 9803, name: 'GrpProduct', title: 'Ürün Yönetimi', hasChild: true, sortOrder: 4 },
  { id: 9804, name: 'GrpTradeMarketing', title: 'Ticari Pazarlama', hasChild: true, sortOrder: 5 },
  { id: 9805, name: 'GrpSalesOps', title: 'Belge Merkezi', hasChild: true, sortOrder: 6 },
  { id: 9806, name: 'GrpFinance', title: 'Finans', hasChild: true, sortOrder: 7 },
  { id: 9807, name: 'GrpStock', title: 'Stok Yönetimi', hasChild: true, sortOrder: 8 },
  // Demirbaş, Saha Yönetimi'nin ALT GRUBUDUR (20260723110000_AddAssetsToFieldManagement).
  { id: 9808, parentID: 9824, name: 'GrpFixtures', title: 'Demirbaş', hasChild: true, sortOrder: 5 },
  { id: 9809, name: 'GrpTraining', title: 'Eğitim', hasChild: true, sortOrder: 10 },
  { id: 9810, name: 'GrpLoyalty', title: 'Sadakat', hasChild: true, sortOrder: 11 },
  { id: 9814, name: 'GrpGamification', title: 'Gamification', hasChild: true, sortOrder: 12 },
  { id: 9811, name: 'GrpCockpit', title: 'Kokpit', hasChild: true, sortOrder: 13 },
  { id: 9812, name: 'GrpReports', title: 'Raporlar', hasChild: true, sortOrder: 14 },

  { id: 9819, name: 'GrpShipmentLogistics', title: 'Sevkiyat ve Lojistik Yönetimi', hasChild: true, sortOrder: 15 },
  { id: 9818, name: 'GrpSystemManagement', title: 'Sistem Yönetimi', hasChild: true, sortOrder: 16 },
  { id: 9960, name: 'GrpDynamicMigration', title: 'Dinamik Migration', hasChild: true, sortOrder: 17 },
  { id: 9961, parentID: 9960, name: 'SoapServices', title: 'Soap Servis', path: '/soap-services', sortOrder: 1 },
  // #252 Genel Parametreler — Distribütörü etkileyen genel ayarlar (Sistem Yönetimi altında).
  { id: 9820, parentID: 9818, name: 'GeneralParameters', title: 'Genel Parametreler', path: '/general-parameters', sortOrder: 90 },



  // --- 1. Dağıtım Kanalı ---
  // 2026-06-03_BackOffice #1: yaprak adı "Distribütör" — üst grup zaten "Dağıtım Kanalı", çoğulunu
  // tekrar etmesi talebin reddiydi. DB'deki başlıkla aynı tutulur (bkz. 20260725130000 migration).
  { id: 9401, parentID: 9801, name: 'Vendors', title: 'Distribütör', path: '/vendors', sortOrder: 1 },
  { id: 9402, parentID: 9801, name: 'Warehouses', title: 'Depolar', path: '/warehouses', sortOrder: 4 },
  { id: 9409, parentID: 9801, name: 'ShipmentVehicles', title: 'Sevkiyat Araçları', path: '/shipment-vehicles', sortOrder: 5 },
  { id: 9410, parentID: 9801, name: 'Distributors', title: 'Distribütörler', path: '/distributors', sortOrder: 6 },

  // --- 1a. Sevkiyat ve Lojistik Yönetimi (#173) ---
  // #213 "Sipariş Planla & Dönüştür" Dağıtım Kanalı'ndan (9801) bu gruba taşındı; planlama önce, yükleme sonra.
  { id: 9423, parentID: 9819, name: 'ShipmentPlanning', title: 'Sipariş Planla & Dönüştür', path: '/shipment-planning', sortOrder: 1 },
  { id: 9094, parentID: 9819, name: 'VehicleLoads', title: 'Araç Yükleme', path: '/vehicle-loads', sortOrder: 2 },

  // --- 1b. Saha Yönetimi (9824) ---
  // Bu grup DB'ye migration ile geldi (20260723100000_AddFieldManagementMenuGroup) ve saha
  // ekranlarını üç ayrı gruptan buraya topladı; fallback listesi o taşımaları yansıtmıyordu.
  // #781 ile Satış Organizasyonu (9813) grubu tamamen boşaldığı için emekliye ayrıldı
  // (20260726100000_MoveSalesOrgMenusToFieldManagement) — son iki ekranı da buranın altında.
  { id: 9422, parentID: 9824, name: 'RoutePlans', title: 'Rut Tanımları', path: '/route-plans', sortOrder: 1 },
  { id: 9121, parentID: 9824, name: 'VisitTracking', title: 'Ziyaret Takip', path: '/visit-tracking', sortOrder: 2 },
  { id: 9403, parentID: 9824, name: 'Representatives', title: 'Satış Temsilcisi', path: '/representatives', sortOrder: 3 },
  { id: 9420, parentID: 9824, name: 'SalesOrgHierarchy', title: 'Satış Organizasyon Hiyerarşisi', path: '/sales-org-hierarchy', sortOrder: 6 },
  { id: 9421, parentID: 9824, name: 'Personnel', title: 'Personel Tanımlama', path: '/personnel', sortOrder: 7 },

  // --- 2. Müşteri Yönetimi ---
  { id: 9201, parentID: 9802, name: 'Customers', title: 'Müşteriler', path: '/customers', sortOrder: 1 },
  { id: 9205, parentID: 9802, name: 'CustomerDynamicGroup', title: 'Müşteri Özelleştirilmiş Grup', path: '/customer-dynamic-groups', sortOrder: 5 },
  { id: 9206, parentID: 9802, name: 'CustomerAdditionalField', title: 'Müşteri Özelleştirilmiş Alan', path: '/customer-additional-fields', sortOrder: 6 },

  // --- 3. Ürün Yönetimi ---
  { id: 9301, parentID: 9803, name: 'Products', title: 'Ürünler', path: '/products', sortOrder: 1 },
  { id: 9544, parentID: 9803, name: 'ProductSegmentationTree', title: 'Ürün Segmentasyon Ağacı', path: '/product-segmentation-tree', sortOrder: 2 },
  { id: 9304, parentID: 9803, name: 'ProductDynamicGroup', title: 'Ürün Özelleştirilmiş Grup', path: '/product-dynamic-groups', sortOrder: 4 },
  { id: 9306, parentID: 9803, name: 'ProductAdditionalField', title: 'Ürün Özelleştirilmiş Alan', path: '/product-additional-fields', sortOrder: 6 },
  { id: 9307, parentID: 9803, name: 'ProductRouting', title: 'Ürün Rotolama', path: '/product-routing', sortOrder: 7 },
  { id: 9308, parentID: 9803, name: 'Producers', title: 'Üreticiler', path: '/producers', sortOrder: 8 },
  { id: 9309, parentID: 9803, name: 'CriticalSku', title: 'Kritik SKU', path: '/critical-skus', sortOrder: 9 },

  // --- 4. Ticari Pazarlama ---
  { id: 9098, parentID: 9804, name: 'Campaigns', title: 'Kampanyalar', path: '/campaigns', sortOrder: 1 },
  { id: 9112, parentID: 9804, name: 'Targets', title: 'Hedef & Teşvik', path: '/targets', sortOrder: 4 },
  { id: 9099, parentID: 9804, name: 'Agreements', title: 'Anlaşmalar', path: '/agreements', sortOrder: 2 },
  // Anketler de Saha Yönetimi'ne taşındı (20260723100000_AddFieldManagementMenuGroup).
  { id: 9100, parentID: 9824, name: 'Surveys', title: 'Anketler', path: '/surveys', sortOrder: 4 },
  { id: 9115, parentID: 9804, name: 'Prices', title: 'Fiyatlar', path: '/prices', sortOrder: 5 },
  { id: 9119, parentID: 9804, name: 'NonSaleReasons', title: 'Satamama Nedenleri', path: '/non-sale-reasons', sortOrder: 6 },

  // --- 5. Satış Operasyon ---
  { id: 9520, parentID: 9805, name: 'SalesTransactions', title: 'Satış İşlemleri', hasChild: true, sortOrder: 1 },
  { id: 9521, parentID: 9805, name: 'PurchaseTransactions', title: 'Alış İşlemleri', hasChild: true, sortOrder: 2 },
  { id: 9522, parentID: 9805, name: 'ReturnTransactions', title: 'İade İşlemleri', hasChild: true, sortOrder: 3 },
  { id: 9527, parentID: 9805, name: 'ServiceInvoices', title: 'Hizmet Faturaları', hasChild: true, sortOrder: 5 },
  //   Satış İşlemleri (9520) alt menüleri (Sipariş → İrsaliye → Fatura):
  { id: 9502, parentID: 9520, name: 'OrderHeaders', title: 'Siparişler', path: '/order-headers', sortOrder: 1 },
  { id: 9533, parentID: 9520, name: 'SalesWaybills', title: 'İrsaliyeler', path: '/waybills', sortOrder: 2 },
  { id: 9503, parentID: 9520, name: 'Invoices', title: 'Faturalar', path: '/invoices', sortOrder: 3 },
  //   Alış / İade İşlemleri alt menüleri (Tür'e göre filtreli sipariş + irsaliye + fatura ekranı):
  { id: 9525, parentID: 9521, name: 'PurchaseOrders', title: 'Siparişler', path: '/purchase-orders', sortOrder: 1 },
  { id: 9534, parentID: 9521, name: 'PurchaseWaybills', title: 'İrsaliyeler', path: '/purchase-waybills', sortOrder: 2 },
  { id: 9523, parentID: 9521, name: 'PurchaseInvoices', title: 'Faturalar', path: '/purchase-invoices', sortOrder: 3 },
  // #833-835 — "Siparişler / İrsaliyeler / Faturalar" artık ÜST BAŞLIK (grup); her biri iade
  // türüne göre üç ayrı yaprağa bölündü. Tür belge alanı değil Type int'idir: Sağlam=2, Bozuk=4,
  // Satıcıya=3. Üst başlıkların Id'si (9526/9535/9524) KORUNUR — mevcut RoleRight satırları
  // onlara bağlı; yalnız path kaldırılıp hasChild açıldı. Migration: 20260730110000_SplitReturnMenusByType.
  { id: 9526, parentID: 9522, name: 'ReturnOrders', title: 'Siparişler', hasChild: true, sortOrder: 1 },
  { id: 9562, parentID: 9526, name: 'ReturnOrdersGood', title: 'Müşteriden Sağlam İade', path: '/return-orders-good', sortOrder: 1 },
  { id: 9563, parentID: 9526, name: 'ReturnOrdersDamaged', title: 'Müşteriden Bozuk İade', path: '/return-orders-damaged', sortOrder: 2 },
  { id: 9564, parentID: 9526, name: 'ReturnOrdersVendor', title: 'Satıcıya İade', path: '/return-orders-vendor', sortOrder: 3 },
  { id: 9535, parentID: 9522, name: 'ReturnWaybills', title: 'İrsaliyeler', hasChild: true, sortOrder: 2 },
  { id: 9565, parentID: 9535, name: 'ReturnWaybillsGood', title: 'Müşteriden Sağlam İade', path: '/return-waybills-good', sortOrder: 1 },
  { id: 9566, parentID: 9535, name: 'ReturnWaybillsDamaged', title: 'Müşteriden Bozuk İade', path: '/return-waybills-damaged', sortOrder: 2 },
  { id: 9567, parentID: 9535, name: 'ReturnWaybillsVendor', title: 'Satıcıya İade', path: '/return-waybills-vendor', sortOrder: 3 },
  { id: 9524, parentID: 9522, name: 'ReturnInvoices', title: 'Faturalar', hasChild: true, sortOrder: 3 },
  { id: 9568, parentID: 9524, name: 'ReturnInvoicesGood', title: 'Müşteriden Sağlam İade', path: '/return-invoices-good', sortOrder: 1 },
  { id: 9569, parentID: 9524, name: 'ReturnInvoicesDamaged', title: 'Müşteriden Bozuk İade', path: '/return-invoices-damaged', sortOrder: 2 },
  { id: 9570, parentID: 9524, name: 'ReturnInvoicesVendor', title: 'Satıcıya İade', path: '/return-invoices-vendor', sortOrder: 3 },
  // Faz 4 — SAHA iade belgesi (ReturnHeader) ekranları. Migration: 20260713131000_AddVendorReturnMenus.
  // 9524 (muhasebenin elle kestiği iade faturası) KORUNUR: Invoice(Type=3) projeksiyonları orada da görünür.
  { id: 9545, parentID: 9522, name: 'VendorReturns', title: 'Satıcıya İade', path: '/vendor-returns', sortOrder: 4 },
  { id: 9546, parentID: 9522, name: 'GibMonitor', title: 'GİB İzleme', path: '/gib-monitor', sortOrder: 5 },
  { id: 9547, parentID: 9522, name: 'ReturnableQuantities', title: 'İade Edilebilir Miktar', path: '/reports/returnable-quantities', sortOrder: 6 },
  // Faz 5 — Depozito iadesi. Migration: 20260713141000_AddDepositReturnMenus (parent 9522).
  // Satıcıya İade'nin (9545) KARDEŞİDİR ama aynı şey değil: o e-fatura kesip GİB'e gider ve tutar
  // taşır; bu GİB'e GİTMEZ, TUTAR TAŞIMAZ — karşılığı depozito bakiyesinin azalmasıdır.
  { id: 9548, parentID: 9522, name: 'DepositReturns', title: 'Depozito İade İrsaliyesi', path: '/deposit-returns', sortOrder: 7 },
  { id: 9549, parentID: 9522, name: 'DepositReconciliation', title: 'Depozito Mutabakatı', path: '/deposit-reconciliation', sortOrder: 8 },
  //   Hizmet Faturaları (9527) alt menüleri (Tür'e göre: Verilen=5 / Alınan=6):
  { id: 9528, parentID: 9527, name: 'ServiceInvoicesGiven', title: 'Verilen Hizmet', path: '/service-invoices-given', sortOrder: 1 },
  { id: 9529, parentID: 9527, name: 'ServiceInvoicesReceived', title: 'Alınan Hizmet', path: '/service-invoices-received', sortOrder: 2 },
  //   Belge Dizaynı (yazdırılabilir düzen tasarımcısı):
  { id: 9540, parentID: 9805, name: 'BelgeDizayni', title: 'Belge Dizaynı', path: '/belge-dizayni', sortOrder: 9 },
  //   Seri Yönetimi (Belge Türü/Tipi bazında matbu seri tanımı):
  { id: 9541, parentID: 9805, name: 'DocumentSeries', title: 'Seri Yönetimi', path: '/document-series', sortOrder: 10 },
  //   E-Belge Kontrol (Faz 6): e-Fatura/e-Arşiv/e-İrsaliye hattının GİB izlemesi — ÜÇ BAĞIMSIZ EKSEN
  //   (servis gönderimi / GİB zarfı / karşı taraf yanıtı). Faz 4'ün "GİB İzleme"si (9546) YALNIZ
  //   satıcıya iade belgelerini gösterir ve İade İşlemleri altında KALIR; bu ekran onun yerine geçmez.
  { id: 9550, parentID: 9805, name: 'EDocumentControl', title: 'E-Belge Kontrol', path: '/e-belge-kontrol', sortOrder: 11 },
  //   Otomasyon: belge kaydedildiğinde karşılık gelen ikinci belgeyi üreten tanımlar.
  //   Ara grup — DB karşılığı 20260728100000_AddDocumentDerivations migration'ındadır.
  { id: 9560, parentID: 9805, name: 'GrpDocumentAutomation', title: 'Otomasyon', hasChild: true, sortOrder: 12 },
  { id: 9561, parentID: 9560, name: 'DocumentDerivations', title: 'Belge Türetme Tanımları', path: '/document-derivations', sortOrder: 1 },
  //   #829/#830 Konsinye — konsinye belgeleri Demirbaş'ın alt başlığı değil BELGEdir; diğer belge
  //   hatlarıyla (Satış/Alış/İade) aynı yerde durmalı. 9138 Demirbaş'tan (9808) buraya taşındı ve
  //   "Konsinye Satış İrsaliyesi" adını aldı; alış ayağı (Type 8) yeni ekran olarak eklendi.
  //   Demirbaş süreçleri bu ekranları kullanmaya devam eder — yalnız menüdeki yeri değişti.
  { id: 9571, parentID: 9805, name: 'GrpConsignment', title: 'Konsinye', hasChild: true, sortOrder: 13 },
  { id: 9138, parentID: 9571, name: 'ConsignmentWaybills', title: 'Konsinye Satış İrsaliyesi', path: '/consignment-waybills', sortOrder: 1 },
  { id: 9572, parentID: 9571, name: 'ConsignmentPurchaseWaybills', title: 'Konsinye Alış İrsaliyesi', path: '/consignment-purchase-waybills', sortOrder: 2 },

  // --- 6. Finans ---
  { id: 9012, parentID: 9806, name: 'LinkRiskTracking', title: 'Risk İzleme', path: '/link-risk-tracking', sortOrder: 1 },
  { id: 9013, parentID: 9806, name: 'PaymentTracking', title: 'Ödeme Takip', path: '/payment', sortOrder: 2 },
  // Bağımsız "Kredi Kartı Tahsilat" (9050) ve "DBS" (9720) kaldırıldı —
  // Alınan Tahsilatlar / Ödemelerim ağaçlarında Kredi Kartı ve DBS zaten var.
  ...financeMenuItems,

  // --- 7. Stok Yönetimi ---
  { id: 9536, parentID: 9807, name: 'Stocks', title: 'Stoklar', path: '/stocks', sortOrder: 0 },
  { id: 9095, parentID: 9807, name: 'VehicleCounts', title: 'Araç Sayım', path: '/vehicle-counts', sortOrder: 2 },
  { id: 9096, parentID: 9807, name: 'DeliveryNotes', title: 'Sevk İrsaliyeleri', path: '/delivery-notes', sortOrder: 3 },
  { id: 9105, parentID: 9807, name: 'DepositCounts', title: 'Depozito Sayımları', path: '/deposit-counts', sortOrder: 4 },
  { id: 9530, parentID: 9807, name: 'StockEntry', title: 'Stok Girişi', path: '/stock-entry', sortOrder: 5 },
  { id: 9531, parentID: 9807, name: 'StockExit', title: 'Stok Çıkışı', path: '/stock-exit', sortOrder: 6 },
  { id: 9532, parentID: 9807, name: 'StockTransfer', title: 'Stok Transferi', path: '/stock-transfer', sortOrder: 7 },
  { id: 9543, parentID: 9807, name: 'WarehouseStockReset', title: 'Depo Stok Sıfırlama', path: '/warehouse-stock-reset', sortOrder: 8 },
  // Faz 3 — araçlar arası transfer (9537) + TRANSIT'te asılı fark (9538). Migration:
  // 20260713121000_AddVehicleTransferMenus. Depo→depo "Stok Transferi"nden (9532) AYRIDIR.
  { id: 9537, parentID: 9807, name: 'VehicleTransfers', title: 'Araç Transferleri', path: '/vehicle-transfers', sortOrder: 9 },
  { id: 9538, parentID: 9807, name: 'TransitBalances', title: 'Transit Bakiye', path: '/transit-balances', sortOrder: 10 },

  // --- 8. Demirbaş ---
  { id: 9101, parentID: 9808, name: 'Coolers', title: 'Soğutucular', path: '/coolers', sortOrder: 1 },
  { id: 9102, parentID: 9808, name: 'CoolerFaults', title: 'Cooler Arızaları', path: '/cooler-faults', sortOrder: 2 },
  { id: 9108, parentID: 9808, name: 'StandCounts', title: 'Soğutucu Sayım', path: '/stand-counts', sortOrder: 3 },
  // Demirbaş Takibi (backend migration ile DB'ye seed edildi; parent 9808)
  { id: 9131, parentID: 9808, name: 'Assets', title: 'Demirbaş Envanteri', path: '/assets', sortOrder: 4 },
  { id: 9132, parentID: 9808, name: 'AssetCounts', title: 'Demirbaş Sayım İzleme', path: '/asset-counts', sortOrder: 5 },
  { id: 9133, parentID: 9808, name: 'AssetCountOrders', title: 'Sayım Tanımı', path: '/asset-count-orders', sortOrder: 6 },
  // Demirbaş Takibi Faz-1 (backend migration ile DB'ye seed edildi; parent 9808)
  { id: 9134, parentID: 9808, name: 'AssetModels', title: 'Demirbaş Tanımı', path: '/asset-models', sortOrder: 7 },
  { id: 9135, parentID: 9808, name: 'AssetAttributeDefs', title: 'Özellik Tanımı', path: '/asset-attribute-defs', sortOrder: 8 },
  { id: 9136, parentID: 9808, name: 'AssetDrafts', title: 'Yeni Demirbaş Onay', path: '/asset-drafts', sortOrder: 9 },
  { id: 9137, parentID: 9808, name: 'AssetImport', title: 'Excel ile İçeri Al', path: '/asset-import', sortOrder: 10 },
  // #829 — 9138 "Konsinye İrsaliye" buradan Belge Merkezi › Konsinye (9571) altına TAŞINDI.

  // --- 9. Eğitim ---
  { id: 9097, parentID: 9809, name: 'Trainings', title: 'Eğitimler', path: '/trainings', sortOrder: 1 },

  // --- 10. Sadakat ---
  { id: 9107, parentID: 9810, name: 'CustomerMemberships', title: 'Üyelik & Puan', path: '/customer-memberships', sortOrder: 1 },

  // --- 11. Gamification (Satış Maratonu) ---
  ...gamificationMenuItems,

  // --- 12. Kokpit (yalnızca üst saha menüleri) ---
  { id: 9089, parentID: 9811, name: 'ChiefDashboard', title: 'Saha Şefi Paneli', path: '/chief-dashboard', sortOrder: 1 },
  { id: 9091, parentID: 9811, name: 'ApprovalRequests', title: 'Onay Talepleri', path: '/approval-requests', sortOrder: 2 },
  { id: 9103, parentID: 9811, name: 'CustomerTasks', title: 'Saha Görevleri', path: '/customer-tasks', sortOrder: 3 },
  { id: 9104, parentID: 9811, name: 'CustomerNotes', title: 'Saha Notları', path: '/customer-notes', sortOrder: 4 },
  { id: 9110, parentID: 9811, name: 'Notifications', title: 'Bildirimler', path: '/notifications', sortOrder: 5 },
  { id: 9601, parentID: 9811, name: 'Announcements', title: 'Duyurular', path: '/announcements', sortOrder: 6 },
  // --- 14. Sistem Yönetimi ---
  // KULLANICI_YETKI_YONETIMI §10.2: Sistem Yönetimi altına iki alt grup açıldı. Taşınan ekranların
  // (9604/9603/9610) Id'si SABİT kalır, yalnız parentID değişir — RoleRight.MenuId/UserRight.MenuId
  // bu Id'lere bağlı olduğundan mevcut yetkiler bozulmaz.
  // NOT: §10.3 tablosunda önerilen 9870/9871 KULLANILMADI — o Id'ler Dashboards grubunda dolu
  // (9870 GrpDashboards, 9871 DashboardGeneral). Boş bloktan 9826/9827 seçildi.
  { id: 9826, parentID: 9818, name: 'GrpUserPermission', title: 'Kullanıcı & Yetki', hasChild: true, sortOrder: 1 },
  { id: 9604, parentID: 9826, name: 'UserManagement', title: 'Kullanıcı Yönetimi', path: '/user-management', sortOrder: 1 },
  // 9611-9618: menü satırları önce açılır (§10.6 adım 4) ki yetki verme ekranlarında görünsünler.
  // React rotası gelene kadar isActive:false → sidebar'da GİZLİ. Ekranı yazan faz hem buradaki
  // bayrağı hem DB'deki Menu.IsActive'i açar.
  { id: 9611, parentID: 9826, name: 'UserGroups', title: 'Kullanıcı Grupları', path: '/user-groups', sortOrder: 2 },
  { id: 9603, parentID: 9826, name: 'RoleManagement', title: 'Rol Yönetimi', path: '/role-management', sortOrder: 3 },
  { id: 9612, parentID: 9826, name: 'PermissionTemplates', title: 'Yetki Şablonları', path: '/permission-templates', sortOrder: 4 },
  { id: 9613, parentID: 9826, name: 'FieldRights', title: 'Kolon Yetkileri', path: '/field-rights', sortOrder: 5 },
  // 9610 Kokpit'ten (9811) buraya taşındı — KK-02 ile mobil yetkinin TEK kaynağı oldu.
  { id: 9610, parentID: 9826, name: 'MobilePermissions', title: 'Mobil Yetkilendirme', path: '/mobile-permissions', sortOrder: 6 },
  { id: 9614, parentID: 9826, name: 'EffectivePermissions', title: 'Etkin Yetki Görüntüleyici', path: '/effective-permissions', sortOrder: 7 },

  { id: 9827, parentID: 9818, name: 'GrpSecurityMonitoring', title: 'Güvenlik & İzleme', hasChild: true, sortOrder: 2 },
  { id: 9615, parentID: 9827, name: 'LoginAudit', title: 'Giriş Günlüğü', path: '/login-audit', sortOrder: 1 },
  { id: 9616, parentID: 9827, name: 'Sessions', title: 'Aktif Oturumlar', path: '/sessions', sortOrder: 2 },
  { id: 9617, parentID: 9827, name: 'Devices', title: 'Cihaz Envanteri', path: '/devices', sortOrder: 3 },
  { id: 9618, parentID: 9827, name: 'AuditLog', title: 'Denetim İzi', path: '/audit-log', sortOrder: 4 },

  { id: 9602, parentID: 9818, name: 'WorkflowManagement', title: 'İş Akışı Yönetimi', path: '/workflows', sortOrder: 3 },
  { id: 9605, parentID: 9818, name: 'EtlPipelines', title: 'ETL Pipeline', path: '/etl-pipelines', sortOrder: 4 },
  { id: 9606, parentID: 9818, name: 'Sync', title: 'Sync', path: '/sync', sortOrder: 5 },
  { id: 9607, parentID: 9818, name: 'Telemetry', title: 'Telemetry', path: '/telemetry', sortOrder: 6 },
  { id: 9701, parentID: 9818, name: 'RuleCategories', title: 'Kural Kategorileri', path: '/rulecategories', sortOrder: 7 },
  { id: 9608, parentID: 9818, name: 'ScheduledTasks', title: 'Zamanlanmış Görevler', path: '/scheduled-tasks', sortOrder: 8 },

  // --- 13. Raporlar (parent-child hierarchy) ---
  // TÜM rapor grupları Sabit Raporlar (9880) ALTINDADIR; eskiden Raporlar'ın (9812) doğrudan
  // çocuklarıydılar (20260728180000_RestructureFixedReportsMenu).
  // Raporu henüz yazılmamış maddelerde path YOK → renderMenuLink '#' sayar, pasif çizer ve
  // menuCaptionFallbacks'ten "Yakında" etiketini basar.
  ...REPORT_MENU,

  // --- Disable edilenler (veride kalır, render'da gizlenir; geri açmak için isActive: true yap) ---
  { id: 9001, name: 'Overview', title: 'Genel Bakış', path: '/dashboard', sortOrder: 90, isActive: false },
  { id: 9710, name: 'MyActions', title: 'Görevlerim', path: '/actions', sortOrder: 91, isActive: false },
  { id: 9700, name: 'RuleManagement', title: 'Kural Yönetimi', path: '/rules', sortOrder: 92, isActive: false },
  { id: 9092, name: 'B2bReturns', title: 'B2B İadeler', path: '/b2b-returns', sortOrder: 93, isActive: false },
];

// "Kural ve Aksiyon Merkezi" — içerik henüz yok. Tek menü öğesi olarak gösterilir; başlığın
// ALTINDA küçük "Yakında" yazısı çıkar ve tıklanınca herhangi bir sayfa açılmaz (pasif).
// #KY-1: ID 9820 "Genel Parametreler"in DB'deki gerçek kimliğidir (20260701131956_AddGeneralParametersMenu).
// Bu istemci-tarafı grup da aynı ID'yi taşıyordu; React key çakışması ve menuId ile yetki çözümlemesinde
// yanlış ekrana bağlanma riski doğuyordu. 9825'e alındı (DB'de karşılığı yok — yalnız fallback görseli).
const ruleActionCenterGroup = {
  id: 9825,
  name: 'GrpRuleActionCenter',
  title: 'Kural ve Aksiyon Merkezi',
  path: '#',
  caption: 'Yakında',
  sortOrder: 14,
  children: [],
};

const footerMenus = [];
const footerMenuNames = new Set(['Help', 'Contact', 'Logout', 'Settings']);
// Backend eski yapı dönerse / fallback'te gösterilmeyecek isimler (route'u olmayan veya dağıtılan eski grup başlıkları)
const removedMenuNames = new Set([
  'SalesRepDashboard',
  'ManagerDashboard',
  'DDS',
  'InvoiceTracking',
  'OrderTracking',
  'OrderDetails',
  'OrderSummaries',
  'InvoiceDetails',
  'EShop',
  'DeliveryNoteTracking',
  'Financials',
  'FieldOperations',
  'Definitions',
  'CustomerManagement',
  'ProductManagement',
  'SalesDistribution',
  'Management',
  'CustomersLegacy',
  'CustomerGroup',
  'CustomerSubGroup',
  'ProductGroup',
  'ProductSubGroup',
  'VendorGroup',
  'VendorSubGroup',
  'RepresentativeGroups',
  'RepresentativeTypes',
]);
const menuTitleFallbacks = {
  MobilePermissions: 'Mobil Yetkilendirme',
  Financials: 'Finans',
  Definitions: 'Tanımlar',
  Management: 'Yönetim',
  SalesDistribution: 'Satış Dağıtım',
  FieldOperations: 'Saha Operasyonları',
  CustomerManagement: 'Müşteri 360',
  Customers: 'Müşteriler',
  CustomerDynamicGroup: 'Müşteri Özelleştirilmiş Grup',
  CustomerAdditionalField: 'Müşteri Özelleştirilmiş Alan',
  ProductManagement: 'Ürün Yönetimi',
  Products: 'Ürünler',
  ProductDynamicGroup: 'Ürün Özelleştirilmiş Grup',
  ProductRestrictions: 'Ürün Kısıtlama',
  CriticalSku: 'Kritik SKU',
  ProductAdditionalField: 'Ürün Özelleştirilmiş Alan',
  Prices: 'Fiyatlar',
  Announcements: 'Duyurular',
  DDS: 'DDS',
  DBS: 'DBS',
  Payment: 'Kredi Karti Tahsilat',
  PaymentTracking: 'Ödeme Takip',
  AccountStatement: 'Hesap Ekstresi Takip',
  OrderTracking: 'Sipariş İzleme',
  DeliveryNoteTracking: 'İrsaliyeler',
  LinkRiskTracking: 'Risk İzleme',
  Representatives: 'Satış Temsilcisi',
  OrderHeaders: 'Siparişler',
  Invoices: 'Faturalar',
  SalesWaybills: 'İrsaliyeler',
  PurchaseWaybills: 'İrsaliyeler',
  ReturnWaybills: 'İrsaliyeler',
  ApprovalRequests: 'Onay Talepleri',
  B2bReturns: 'B2B İadeler',
  Visits: 'Ziyaretler',
  VehicleLoads: 'Araç Yükleme',
  VehicleCounts: 'Araç Sayım',
  DeliveryNotes: 'Sevk İrsaliyeleri',
  Trainings: 'Eğitimler',
  Campaigns: 'Kampanyalar',
  CampaignAuthorizations: 'Kampanya Yetkileri',
  Targets: 'Hedef & Teşvik',
  Agreements: 'Anlaşmalar',
  Surveys: 'Anketler',
  Coolers: 'Soğutucular',
  CoolerFaults: 'Cooler Arızaları',
  CustomerTasks: 'Saha Görevleri',
  CustomerNotes: 'Saha Notları',
  DepositCounts: 'Depozito Sayımları',
  RoutePlans: 'Rut Tanımları',
  ShipmentPlanning: 'Sipariş Planla & Dönüştür',
  CustomerMemberships: 'Üyelik & Puan',
  StandCounts: 'Soğutucu Sayım',
  Reports: 'Raporlar',
  ChiefDashboard: 'Saha Şefi Paneli',
  Notifications: 'Bildirimler',
  SalesAnalytics: 'Satış Analizi',
  Stocks: 'Stoklar',
  // Sabit Raporlar ağacı — başlıkların TEK kaynağı REPORT_MENU; buradaki eşleme yalnız
  // DB'den başlıksız gelen satırlar için yedektir (aşağıda REPORT_MENU'den türetilir).
  ...Object.fromEntries(REPORT_MENU.map((m) => [m.name, m.title])),
  VehicleTransfers: 'Araç Transferleri',
  TransitBalances: 'Transit Bakiye',
  VendorReturns: 'Satıcıya İade',
  GibMonitor: 'GİB İzleme',
  ReturnableQuantities: 'İade Edilebilir Miktar',
  DepositReturns: 'Depozito İade İrsaliyesi',
  DepositReconciliation: 'Depozito Mutabakatı',
};
const forcedMenuLabels = new Set([
  'MobilePermissions',
  'OrderHeaders', 'AccountStatement', 'Payment', 'PaymentTracking', 'DeliveryNoteTracking', 'Announcements',
  'Representatives',
  'FieldOperations', 'ApprovalRequests', 'B2bReturns', 'Visits', 'FieldVisits',
  'Stocks', 'VehicleLoads', 'VehicleCounts', 'DeliveryNotes',
'Trainings', 'Campaigns', 'CampaignAuthorizations', 'Agreements', 'Surveys', 'Coolers', 'CoolerFaults',
'CustomerTasks', 'CustomerNotes', 'DepositCounts', 'CustomerMemberships', 'StandCounts', 'Reports', 'ChiefDashboard', 'Notifications', 'SalesAnalytics',
'ProductManagement', 'ProductDynamicGroup', 'ProductRestrictions', 'ProductAdditionalField', 'Prices',
'CustomerManagement', 'Customers', 'CustomerDynamicGroup', 'CustomerAdditionalField',
'ShipmentPlanning',
]);
function SidebarIcon({ name, title }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '2',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };

  const iconName = name || title;

  // --- Dashboards (Rona AI) ---
  // Grup: gösterge kadranı + AI parıltısı — "ölçüm + asistan" ikisini birden anlatır.
  if (iconName === 'GrpDashboards') {
    return (
      <svg {...common}>
        <path d="M4 17a8 8 0 0 1 14.5-4.7" />
        <path d="M12 17l3.5-3.5" />
        <circle cx="12" cy="17" r="1.2" fill="currentColor" stroke="none" />
        <path d="M18.6 3.2l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7z" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (iconName === 'DashboardGeneral') {
    return <svg {...common}><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="10" width="7" height="11" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></svg>;
  }
  if (iconName === 'DashboardCustomer') {
    return <svg {...common}><circle cx="9" cy="8" r="3.2" /><path d="M3 20v-1.2A4.8 4.8 0 0 1 7.8 14h2.4a4.8 4.8 0 0 1 4.8 4.8V20" /><path d="M17 5.5a3 3 0 0 1 0 5.6" /><path d="M21 20v-1a3.6 3.6 0 0 0-2.6-3.4" /></svg>;
  }
  if (iconName === 'DashboardProduct') {
    return <svg {...common}><path d="M12 3l8 4.2v9.6L12 21l-8-4.2V7.2L12 3z" /><path d="M4.2 7.3L12 11.5l7.8-4.2" /><path d="M12 11.5V21" /></svg>;
  }
  if (iconName === 'DashboardFinance') {
    return <svg {...common}><rect x="2.5" y="6" width="19" height="12" rx="2.5" /><circle cx="12" cy="12" r="2.6" /><path d="M6 10v4" /><path d="M18 10v4" /></svg>;
  }
  if (iconName === 'DashboardField') {
    return <svg {...common}><path d="M12 21s6.5-5.6 6.5-10a6.5 6.5 0 1 0-13 0C5.5 15.4 12 21 12 21z" /><circle cx="12" cy="11" r="2.4" /></svg>;
  }

  if (iconName === 'Financials' || iconName === 'GrpFinance') {
    return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 6v12" /><path d="M16 9.5C15.2 8.6 13.8 8 12.3 8H11a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4h-1.3c-1.5 0-2.9-.6-3.7-1.5" /></svg>;
  }
  if (iconName === 'SalesDistribution' || iconName === 'GrpDistribution') {
    return <svg {...common}><path d="M3 7h10v10H3z" /><path d="M13 10h4l3 3v4h-7z" /><circle cx="7" cy="19" r="2" /><circle cx="17" cy="19" r="2" /><path d="M6 4h8" /></svg>;
  }
  if (iconName === 'Definitions') {
    return <svg {...common}><path d="M4 5h16v14H4z" /><path d="M8 5v14" /><path d="M12 9h5" /><path d="M12 13h5" /></svg>;
  }
  if (iconName === 'Management') {
    return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V22h-4v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H2v-4h.2a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V2h4v.2a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 0 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1h.2v4h-.2a1.7 1.7 0 0 0-1.5 1z" /></svg>;
  }
  if (iconName === 'Payment') {
    return <svg {...common}><path d="M3 10h18" /><path d="M5 10V7l7-4 7 4v3" /><path d="M6 10v8" /><path d="M10 10v8" /><path d="M14 10v8" /><path d="M18 10v8" /><path d="M4 18h16" /></svg>;
  }
  if (iconName === 'DDS' || iconName === 'DBS') {
    return <svg {...common}><ellipse cx="12" cy="5" rx="7" ry="3" /><path d="M5 5v6c0 1.7 3.1 3 7 3s7-1.3 7-3V5" /><path d="M5 11v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></svg>;
  }
  if (iconName === 'OrderTracking' || iconName === 'GrpSalesOps') {
    return <svg {...common}><path d="M6 3h11l3 3v15H6z" /><path d="M17 3v4h4" /><circle cx="11" cy="12" r="3" /><path d="M13.5 14.5L16 17" /></svg>;
  }
  if (iconName === 'SalesTransactions') {
    return <svg {...common}><circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" /><path d="M2 3h3l2.4 12.2a1 1 0 0 0 1 .8h8.6a1 1 0 0 0 1-.8L21 7H6" /></svg>;
  }
  if (iconName === 'PurchaseTransactions') {
    return <svg {...common}><path d="M6 7h12l-1 13H7z" /><path d="M9 7a3 3 0 0 1 6 0" /><path d="M12 11v5" /><path d="M9.5 13.5h5" /></svg>;
  }
  if (iconName === 'ReturnTransactions') {
    return <svg {...common}><path d="M9 14L4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 5 5v1a5 5 0 0 1-5 5h-3" /></svg>;
  }
  if (iconName === 'OrderHeaders') {
    return <svg {...common}><path d="M8 4h8l2 3v14H6V7z" /><path d="M8 4v3h10" /><path d="M9 12h6" /><path d="M9 16h4" /></svg>;
  }
  if (iconName === 'OrderDetails') {
    return <svg {...common}><path d="M5 4h14v16H5z" /><path d="M8 8h8" /><path d="M8 12h8" /><path d="M8 16h5" /></svg>;
  }
  if (iconName === 'OrderSummaries') {
    return <svg {...common}><path d="M4 19V5" /><path d="M4 19h16" /><path d="M8 16v-5" /><path d="M12 16V8" /><path d="M16 16v-3" /></svg>;
  }
  if (iconName === 'CustomerManagement' || iconName === 'GrpCustomer') {
    return <svg {...common}><circle cx="9" cy="8" r="3" /><path d="M3 20v-1a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v1" /><circle cx="17" cy="9" r="2" /><path d="M15 20v-1a3 3 0 0 1 3-3h2" /></svg>;
  }
  if (iconName === 'Representatives') {
    return <svg {...common}><circle cx="10" cy="7" r="4" /><path d="M4 21v-2a5 5 0 0 1 5-5h2" /><path d="M15 14h6v6h-6z" /><path d="M17 17h2" /></svg>;
  }
  if (iconName === 'Vendors') {
    return <svg {...common}><path d="M4 9h16l-1 10H5L4 9z" /><path d="M7 9V6h10v3" /><path d="M8 13h8" /></svg>;
  }
  if (iconName === 'Warehouses') {
    return <svg {...common}><path d="M3 10l9-5 9 5v10H3z" /><path d="M7 20v-6h10v6" /><path d="M9 14h6" /></svg>;
  }
  if (iconName === 'Stocks' || iconName === 'GrpStock') {
    return <svg {...common}><path d="M3 7l9-4 9 4-9 4-9-4z" /><path d="M3 12l9 4 9-4" /><path d="M3 17l9 4 9-4" /></svg>;
  }
  if (iconName === 'WarehouseStockReset') {
    return <svg {...common}><path d="M3 10l9-5 9 5v10H3z" /><path d="M7 20v-6h10v6" /><path d="M12 8v8" /><path d="M9 14l3 3 3-3" /></svg>;
  }
  if (iconName === 'GrpSalesOrg') {
    return <svg {...common}><circle cx="9" cy="7" r="3" /><path d="M3 21v-1a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v1" /><path d="M16 3.5a3 3 0 0 1 0 6" /><path d="M21 21v-1a4 4 0 0 0-3-3.8" /></svg>;
  }
  if (iconName === 'GrpSystemManagement') {
    return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V22h-4v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H2v-4h.2a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V2h4v.2a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 0 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1h.2v4h-.2a1.7 1.7 0 0 0-1.5 1z" /></svg>;
  }
  if (iconName === 'GrpRuleActionCenter') {
    return <svg {...common}><path d="M4 6h9" /><path d="M4 12h6" /><path d="M4 18h9" /><circle cx="17.5" cy="9" r="2.5" /><circle cx="14.5" cy="18" r="2.5" /></svg>;
  }
  switch (iconName) {
    case 'Overview':
    case 'Dashboard':
      return <svg {...common}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>;
    case 'Financials':
    case 'SalesDistribution':
      return <svg {...common}><polyline points="3 17 9 11 13 15 21 7" /><polyline points="14 7 21 7 21 14" /></svg>;
    case 'InvoiceTracking':
    case 'AccountStatement':
    case 'Invoices':
    case 'InvoiceDetails':
      return <svg {...common}><path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2z" /><path d="M9 8h6" /><path d="M9 12h6" /><path d="M9 16h4" /></svg>;
    case 'LinkRiskTracking':
      return <svg {...common}><path d="M10 13a5 5 0 0 0 7.07 0l2.12-2.12a5 5 0 0 0-7.07-7.07L11 4.93" /><path d="M14 11a5 5 0 0 0-7.07 0L4.81 13.12a5 5 0 0 0 7.07 7.07L13 19.07" /></svg>;
    case 'PaymentTracking':
    case 'Payment':
      return <svg {...common}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /><path d="M6 15h4" /></svg>;
    case 'DDS':
      return <svg {...common}><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 8h8" /><path d="M8 12h8" /><path d="M8 16h5" /></svg>;
    case 'OrderTracking':
    case 'MyActions':
    case 'OrderHeaders':
    case 'OrderDetails':
    case 'OrderSummaries':
      return <svg {...common}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
    case 'EShop':
      return <svg {...common}><path d="M6 2l1.5 4h9L18 2" /><path d="M3 6h18l-2 14H5L3 6z" /><path d="M8 10a4 4 0 0 0 8 0" /></svg>;
    case 'DeliveryNoteTracking':
      return <svg {...common}><path d="M21 8v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" /><path d="M1 8l11-6 11 6" /><path d="M9 14l2 2 4-5" /></svg>;
    case 'Announcements':
      return <svg {...common}><path d="M3 11v2a2 2 0 0 0 2 2h3l5 4V5L8 9H5a2 2 0 0 0-2 2z" /><path d="M16 9a4 4 0 0 1 0 6" /></svg>;
    case 'GrpProduct':
    case 'ProductManagement':
      return <svg {...common}><path d="M16.5 9.4 7.5 4.2" /><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.7z" /><path d="M3.3 7 12 12l8.7-5" /><path d="M12 22V12" /></svg>;
    case 'Products':
    case 'ProductListReport':
      return <svg {...common}><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.7z" /><path d="M3.3 7L12 12l8.7-5" /><path d="M12 22V12" /></svg>;
    case 'ProductDynamicGroup':
    case 'CustomerDynamicGroup':
      return <svg {...common}><path d="M3 7h7v7H3z" /><path d="M14 3h7v7h-7z" /><path d="M17.5 14v7" /><path d="M14 17.5h7" /><path d="M6.5 17.5h.01" /></svg>;
    case 'ProductAdditionalField':
    case 'CustomerAdditionalField':
      return <svg {...common}><path d="M4 5h16v14H4z" /><path d="M8 9h8" /><path d="M8 13h5" /><path d="M16.5 14.5v4" /><path d="M14.5 16.5h4" /></svg>;
    case 'Prices':
    case 'PriceListReport':
      return <svg {...common}><path d="M12 2v20" /><path d="M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>;
    case 'Customers':
    case 'Representatives':
    case 'RepresentativeListReport':
      return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9" /><path d="M16 3.1a4 4 0 0 1 0 7.8" /></svg>;
    case 'Vendors':
    case 'Warehouses':
    case 'WarehouseListReport':
      return <svg {...common}><path d="M3 7h11v10H3z" /><path d="M14 10h4l3 3v4h-7z" /><circle cx="7" cy="19" r="2" /><circle cx="17" cy="19" r="2" /></svg>;
    case 'Management':
    case 'Definitions':
      return <svg {...common}><path d="M12 2l7 4v6c0 5-3 8-7 10-4-2-7-5-7-10V6l7-4z" /><path d="M9 12l2 2 4-5" /></svg>;
    case 'Operations':
    case 'FieldOperations':
      return <svg {...common}><path d="M13 2L3 14h8l-1 8 11-13h-8l0-7z" /></svg>;
    case 'ApprovalRequests':
      return <svg {...common}><path d="M12 2l7 4v6c0 5-3 8-7 10-4-2-7-5-7-10V6l7-4z" /><path d="M9 12l2 2 4-5" /></svg>;
    case 'B2bReturns':
      return <svg {...common}><path d="M9 14l-4-4 4-4" /><path d="M5 10h11a4 4 0 0 1 4 4v0a4 4 0 0 1-4 4H9" /></svg>;
    case 'Visits':
      return <svg {...common}><path d="M12 22s-7-7-7-13a7 7 0 0 1 14 0c0 6-7 13-7 13z" /><circle cx="12" cy="9" r="2.5" /></svg>;
    case 'VehicleLoads':
      return <svg {...common}><path d="M3 7h11v9H3z" /><path d="M14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" /><path d="M6 4h7" /></svg>;
    case 'GrpShipmentLogistics':
    case 'VehicleListReport':
    case 'ShipmentVehicles':
      return <svg {...common}><path d="M1 6h13v10H1z" /><path d="M14 9h4l3 3v4h-7z" /><circle cx="6" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></svg>;
    case 'Distributors':
    case 'DistributorListReport':
    case 'DistChannelListReport':
      return <svg {...common}><circle cx="12" cy="5" r="2.5" /><circle cx="5" cy="18" r="2.5" /><circle cx="19" cy="18" r="2.5" /><path d="M12 7.5v4" /><path d="M12 11.5L6.5 16" /><path d="M12 11.5L17.5 16" /></svg>;
    case 'VehicleCounts':
      return <svg {...common}><path d="M9 3h6v4H9z" /><path d="M4 7h16v14H4z" /><path d="M8 12h8" /><path d="M8 16h5" /></svg>;
    case 'DeliveryNotes':
    case 'WaybillListReport':
      return <svg {...common}><path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2z" /><path d="M9 8h6" /><path d="M9 12h6" /><path d="M9 16h4" /></svg>;
    case 'GrpTraining':
    case 'Trainings':
      return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M10 9l5 3-5 3z" fill="currentColor" /></svg>;
    case 'GrpTradeMarketing':
    case 'CampaignListReport':
    case 'Campaigns':
      return <svg {...common}><path d="M20 12v9H4v-9" /><path d="M22 7H2v5h20V7z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></svg>;
    case 'Agreements':
      return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 13l2 2 4-4" /></svg>;
    case 'Surveys':
      return <svg {...common}><path d="M9 11l2 2 4-4" /><rect x="4" y="4" width="16" height="16" rx="2" /></svg>;
    case 'GrpFixtures':
    case 'Coolers':
      return <svg {...common}><rect x="6" y="3" width="12" height="18" rx="2" /><path d="M6 12h12" /><path d="M9 7h.01" /><path d="M9 16h.01" /></svg>;
    case 'CoolerFaults':
      return <svg {...common}><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>;
    case 'CustomerTasks':
      return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 9l2 2 4-4" /><path d="M9 15h6" /></svg>;
    case 'CustomerNotes':
      return <svg {...common}><path d="M14 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-9z" /><path d="M14 3v6h6" /><path d="M8 13h6" /><path d="M8 17h4" /></svg>;
    case 'DepositCounts':
      return <svg {...common}><path d="M7 19H4.8a2 2 0 0 1-1.7-3l3.2-5.5" /><path d="M11 19h6.2a2 2 0 0 0 1.7-3l-1.1-1.9" /><path d="M13.5 5L12 2.5 10.5 5" /><path d="M7.5 10.5L9 8l3 5" /><polyline points="9 19 7 19 7 17" /><polyline points="16 13.5 17 15 18.5 14" /></svg>;
    case 'GrpLoyalty':
    case 'CustomerMemberships':
      return <svg {...common}><circle cx="12" cy="8" r="5" /><path d="M8.5 12.5L7 21l5-3 5 3-1.5-8.5" /></svg>;
    case 'StandCounts':
      return <svg {...common}><path d="M14 14.76V5a2 2 0 0 0-4 0v9.76a4 4 0 1 0 4 0z" /><path d="M12 10v5" /></svg>;
    case 'GrpReports':
    case 'Reports':
      return <svg {...common}><path d="M3 3v18h18" /><rect x="7" y="11" width="3" height="6" /><rect x="12" y="7" width="3" height="10" /><rect x="17" y="13" width="3" height="4" /></svg>;
    // Saha Ziyaretleri (Sabit Raporlar › Ziyaret): sahadaki temsilci — konum iğnesi + kişi.
    case 'FieldVisits':
      return <svg {...common}><path d="M9 12.5s3.6-3.1 3.6-5.6a3.6 3.6 0 1 0-7.2 0c0 2.5 3.6 5.6 3.6 5.6z" /><circle cx="9" cy="6.9" r="1.2" /><circle cx="17" cy="9" r="2.4" /><path d="M13 20v-1.4a3.6 3.6 0 0 1 3.6-3.6h.8a3.6 3.6 0 0 1 3.6 3.6V20" /></svg>;
    // Ziyaret Takip (Sabit Raporlar › Özet Dashboard): gösterge kadranı + konum iğnesi.
    case 'VisitTracking':
      return <svg {...common}><path d="M3 14a9 9 0 0 1 18 0" /><path d="M3 14v2a2 2 0 0 0 2 2h5" /><path d="M17 21s3.5-3.4 3.5-6a3.5 3.5 0 1 0-7 0c0 2.6 3.5 6 3.5 6z" /><circle cx="17" cy="15" r="1.2" /></svg>;

    // --- Raporlar ağacı: her kalemin konusunu anlatan ikonlar (yoksa baş harflere düşüyordu) ---
    // Sabit Raporlar grubu: pano + mini sütun grafik — "hazır, kriterli rapor".
    case 'GrpReportFixed':
      return <svg {...common}><rect x="4" y="4" width="16" height="17" rx="2" /><path d="M9 3h6v3H9z" /><path d="M9.5 17v-4" /><path d="M14.5 17v-7" /></svg>;
    // Ziyaret Analizi: konum iğnesi + sütunlar (ziyaretin ölçülmesi).
    case 'DocumentVisitAnalysis':
      return <svg {...common}><path d="M8 13s4-3.4 4-6.1A4 4 0 0 0 4 6.9C4 9.6 8 13 8 13z" /><circle cx="8" cy="6.9" r="1.3" /><path d="M14 20v-6" /><path d="M19 20v-10" /></svg>;
    // ST Rut Tanım Listesi: iki durak arası rota.
    case 'STRoutePlanList':
      return <svg {...common}><circle cx="6" cy="18" r="2.5" /><circle cx="18" cy="6" r="2.5" /><path d="M18 8.5c0 5-12 2.5-12 7" /></svg>;
    // Rut Tanım Listesi Raporu: duraklar + karşılarındaki satırlar (rotanın liste hali).
    case 'RoutePlanDetailReport':
      return <svg {...common}><circle cx="6" cy="6" r="2" /><circle cx="6" cy="18" r="2" /><path d="M6 8v8" /><path d="M11 6h9" /><path d="M11 18h9" /></svg>;
    // Demirbaş Son Durum: soğutucu + onay (sahadaki demirbaşın durumu).
    case 'AssetStatusReport':
      return <svg {...common}><rect x="4" y="3" width="10" height="18" rx="2" /><path d="M4 11h10" /><path d="M7 7h.01" /><path d="M15.5 17.5l2 2 4-4" /></svg>;
    // Stok Ekstresi (Detay): katman katman stok hareketi.
    case 'StockLedgerReport':
      return <svg {...common}><path d="M12 3l8 4-8 4-8-4 8-4z" /><path d="M4 11l8 4 8-4" /><path d="M4 16l8 4 8-4" /></svg>;
    // Ziyaret Detay Raporu: belge + konum iğnesi.
    case 'VisitDetailReport':
      return <svg {...common}><path d="M5 3h9l4 4v14H5z" /><path d="M14 3v4h4" /><path d="M12 12.4c0 1.9-2.3 4.1-2.3 4.1s-2.3-2.2-2.3-4.1a2.3 2.3 0 1 1 4.6 0z" /></svg>;
    // Güncel Stok Raporu: kutu + saat — "şu andaki" bakiye.
    case 'CurrentStockReport':
      return <svg {...common}><path d="M3 7l5.5-3L14 7l-5.5 3L3 7z" /><path d="M3 7v7l5.5 3L14 14V7" /><circle cx="18" cy="17" r="4" /><path d="M18 15v2.2l1.5 1" /></svg>;

    // Tanımlamalar grubu: şablon yerleşimi.
    case 'GrpReportTemplate':
      return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /><path d="M9 9v11" /></svg>;
    case 'TemplateCustomer':
      return <svg {...common}><circle cx="12" cy="8" r="3.5" /><path d="M5 20v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1" /></svg>;
    case 'TemplateInvoice':
      return <svg {...common}><path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3z" /><path d="M9 9h6" /><path d="M9 13h6" /></svg>;

    // Belge grubu: klasik dosya.
    case 'GrpReportDocument':
      return <svg {...common}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /></svg>;
    case 'DocumentOrderList':
      return <svg {...common}><rect x="4" y="4" width="16" height="17" rx="2" /><path d="M9 3h6v3H9z" /><path d="M8.5 12l1.8 1.8L14 10" /><path d="M8.5 17h7" /></svg>;

    // Analiz Listeleri grubu: trend çizgisi.
    case 'GrpReportAnalysis':
      return <svg {...common}><path d="M3 3v18h18" /><path d="M7 15l3.5-3.5 3 3L20 7" /><circle cx="20" cy="7" r="1.3" fill="currentColor" stroke="none" /></svg>;
    case 'SalesDetailReport':
      return <svg {...common}><path d="M5 3h9l4 4v14H5z" /><path d="M14 3v4h4" /><path d="M8.5 17l2.5-3 2 2 3-4" /></svg>;
    // Tahsilat: cüzdan.
    case 'CollectionDetailReport':
      return <svg {...common}><path d="M3 8h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z" /><path d="M3 8V6a2 2 0 0 1 2-2h10" /><circle cx="16" cy="13.5" r="1.4" /></svg>;
    // Finans grubu: banknot.
    case 'GrpReportFinance':
    case 'CashCollectionReport':
      return <svg {...common}><rect x="2.5" y="6" width="19" height="12" rx="2" /><circle cx="12" cy="12" r="2.6" /><path d="M6 10v4" /><path d="M18 10v4" /></svg>;

    // --- Sabit Raporlar: yeni gruplar ve maddeler ---
    case 'GrpReportVisit':                       // Ziyaret grubu: konum iğnesi
      return <svg {...common}><path d="M12 21s6.5-5.6 6.5-10a6.5 6.5 0 1 0-13 0C5.5 15.4 12 21 12 21z" /><circle cx="12" cy="11" r="2.3" /></svg>;
    case 'GrpReportMovement':                    // Hareket Kayıtları: giren/çıkan oklar
      return <svg {...common}><path d="M4 8h12" /><path d="M13 5l3 3-3 3" /><path d="M20 16H8" /><path d="M11 13l-3 3 3 3" /></svg>;
    case 'GrpReportDashboard':                   // Özet Dashboard: gösterge kadranı
      return <svg {...common}><path d="M3 13a9 9 0 0 1 18 0" /><path d="M3 13v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" /><path d="M12 13l4-3" /></svg>;
    case 'GrpReportLegacy':                      // Eski Raporlar: arşiv kutusu
      return <svg {...common}><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><path d="M10 12h4" /></svg>;
    case 'StockInReport':                        // Stok Giriş: kutuya inen ok
      return <svg {...common}><path d="M12 3v8" /><path d="M8.5 7.5L12 11l3.5-3.5" /><path d="M4 13v6a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6" /></svg>;
    case 'StockOutReport':                       // Stok Çıkış: kutudan çıkan ok
      return <svg {...common}><path d="M12 11V3" /><path d="M8.5 6.5L12 3l3.5 3.5" /><path d="M4 13v6a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6" /></svg>;
    case 'StockTransferReport':                  // Stok Transferi: iki depo arası ok
      return <svg {...common}><rect x="2.5" y="9" width="7" height="7" rx="1.5" /><rect x="14.5" y="9" width="7" height="7" rx="1.5" /><path d="M10 12.5h4" /><path d="M12.5 11l1.5 1.5-1.5 1.5" /></svg>;
    case 'TransferCollectionReport':             // Havale/EFT: hesaplar arası transfer
      return <svg {...common}><path d="M4 8h13" /><path d="M14 5l3 3-3 3" /><path d="M20 16H7" /><path d="M10 13l-3 3 3 3" /></svg>;
    case 'CreditCardCollectionReport':
      return <svg {...common}><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /><path d="M6 15h4" /></svg>;
    case 'ChequeReport':                         // Çek: imzalı yaprak
      return <svg {...common}><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M6 11h6" /><path d="M6 14.5c2 0 2.5-2 4-2s2 3 4 1.5" /></svg>;
    case 'PromissoryNoteReport':                 // Senet: mühürlü belge
      return <svg {...common}><path d="M5 3h9l4 4v14H5z" /><path d="M14 3v4h4" /><circle cx="12" cy="14" r="2.6" /><path d="M9 20l3-1.5 3 1.5" /></svg>;
    case 'DbsReport':
      return <svg {...common}><ellipse cx="12" cy="5" rx="7" ry="3" /><path d="M5 5v6c0 1.7 3.1 3 7 3s7-1.3 7-3V5" /><path d="M5 11v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></svg>;
    case 'BalanceReport':                        // Borç–Alacak: terazi
      return <svg {...common}><path d="M12 4v16" /><path d="M5 8h14" /><path d="M8 20h8" /><path d="M5 8l-2.5 5h5L5 8z" /><path d="M19 8l-2.5 5h5L19 8z" /></svg>;
    case 'StockLedgerSummaryReport':             // Stok Ekstresi Özet: katman + özet çizgi
      return <svg {...common}><path d="M12 3l8 4-8 4-8-4 8-4z" /><path d="M4 12l8 4 8-4" /><path d="M8 20h8" /></svg>;
    case 'CustomerStatementSummaryReport':       // Hesap Ekstresi Özet: kişi + satırlar
      return <svg {...common}><circle cx="8" cy="7" r="3" /><path d="M3 19v-1a4 4 0 0 1 4-4h2" /><path d="M13 9h8" /><path d="M13 13h8" /><path d="M13 17h5" /></svg>;
    case 'CustomerProductMovementReport':        // Müşteri–Ürün analizi: kişi + kutu
      return <svg {...common}><circle cx="7" cy="7" r="3" /><path d="M2 19v-1a4 4 0 0 1 4-4h2" /><path d="M17 8l5 2.5-5 2.5-5-2.5L17 8z" /><path d="M12 10.5V16l5 2.5 5-2.5v-5.5" /></svg>;

    case 'GrpGamification':
    case 'Leaderboard':
      return <svg {...common}><path d="M8 21h8" /><path d="M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M17 5h3v2a3 3 0 0 1-3 3" /><path d="M7 5H4v2a3 3 0 0 0 3 3" /></svg>;
    case 'GamificationBadges':
      return <svg {...common}><circle cx="12" cy="8" r="5" /><path d="M8.5 12.5L7 21l5-3 5 3-1.5-8.5" /></svg>;
    case 'GrpCockpit':
    case 'ChiefDashboard':
      return <svg {...common}><path d="M12 14l4-4" /><path d="M3 12a9 9 0 0 1 18 0" /><path d="M3 12v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3" /><circle cx="12" cy="12" r="1.2" fill="currentColor" /></svg>;
    case 'Notifications':
      return <svg {...common}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>;
    case 'SalesAnalytics':
      return <svg {...common}><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>;
    case 'WorkflowManagement':
      return <svg {...common}><rect x="3" y="3" width="6" height="6" rx="1" /><rect x="15" y="3" width="6" height="6" rx="1" /><rect x="9" y="15" width="6" height="6" rx="1" /><path d="M9 6h6" /><path d="M18 9v3l-6 3" /><path d="M6 9v3l6 3" /></svg>;
    case 'EtlPipelines':
      return <svg {...common}><path d="M4 4h6v6H4z" /><path d="M14 14h6v6h-6z" /><path d="M10 7h4a3 3 0 0 1 3 3v4" /><path d="M7 10v4a3 3 0 0 0 3 3h4" /></svg>;
    case 'RuleCategories':
      return <svg {...common}><path d="M20 10v10H4V4h10" /><path d="M14 4h6v6" /><path d="M9 9h4" /><path d="M9 13h6" /><path d="M9 17h4" /></svg>;
    case 'RuleManagement':
      return <svg {...common}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
    case 'RoleManagement':
      return <svg {...common}><path d="M12 2l7 4v6c0 5-3 8-7 10-4-2-7-5-7-10V6l7-4z" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>;
    case 'UserManagement':
      return <svg {...common}><circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4" /><path d="M17 11v6" /><path d="M20 14h-6" /></svg>;
    case 'MobilePermissions':
      return <svg {...common}><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /><path d="M9 10l2 2 4-4" /></svg>;
    case 'Sync':
      return <svg {...common}><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M3 16h5" /><path d="M16 8h5" /></svg>;
    case 'Telemetry':
      return <svg {...common}><path d="M3 3v18h18" /><path d="M7 15l3-3 3 2 5-7" /><path d="M7 19v-4" /><path d="M13 19v-5" /><path d="M19 19v-8" /></svg>;
    case 'SalesRepDashboard':
    case 'ManagerDashboard':
      return <svg {...common}><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-7" /></svg>;
    case 'Settings':
      return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V22a2 2 0 1 1-4 0v-.2a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H2a2 2 0 1 1 0-4h.2a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V2a2 2 0 1 1 4 0v.2a1.7 1.7 0 0 0 1 1.5h.1a1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.2a1.7 1.7 0 0 0-1.5 1z" /></svg>;
    case 'Help':
      return <svg {...common}><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 1 1 5.8 1c-.6 1-1.7 1.4-2.2 2.2-.3.4-.4.8-.4 1.3" /><path d="M12 17h.01" /></svg>;
    case 'Contact':
      return <svg {...common}><path d="M4 4h16v16H4z" /><path d="M4 7l8 6 8-6" /></svg>;
    case 'Logout':
      return <svg {...common}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>;
    case 'Producers':
      return <svg {...common}><path d="M3 21h18" /><path d="M4 21V10l6 4V10l6 4V8l4 2v11" /><path d="M8 21v-4h3v4" /></svg>;

    // ===== Alış / Satış / İade belgeleri =====
    // Ortak dil: kamyon = irsaliye, belge = fatura, pano = sipariş.
    // Yön oku belgenin tarafını söyler: aşağı = alış (giren), yukarı = satış (çıkan),
    // geri dönen ok = iade. Sağlam/Hasarlı/Tedarikçi türevleri ayırt edici bir işaret alır.
    case 'SalesWaybills':
      return <svg {...common}><path d="M3 7h9v9H3z" /><path d="M12 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.8" /><circle cx="16.5" cy="18" r="1.8" /><path d="M6 4.5h4" /></svg>;
    case 'PurchaseWaybills':
      return <svg {...common}><path d="M3 9h9v7H3z" /><path d="M12 11h4l3 3v2h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="16.5" cy="18" r="1.6" /><path d="M7.5 2v4" /><path d="M5.8 4.4L7.5 6.2 9.2 4.4" /></svg>;
    case 'PurchaseInvoices':
      return <svg {...common}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M12 10v5" /><path d="M10 13.2l2 2 2-2" /></svg>;
    case 'PurchaseOrders':
      return <svg {...common}><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 3h6v3H9z" /><path d="M12 10v5" /><path d="M10 13.2l2 2 2-2" /></svg>;
    case 'ReturnInvoices':
      return <svg {...common}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M14 14H9.5" /><path d="M11.5 11.8L9.3 14l2.2 2.2" /></svg>;
    case 'ReturnOrders':
      return <svg {...common}><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 3h6v3H9z" /><path d="M15 14h-5" /><path d="M12 11.8L9.8 14l2.2 2.2" /></svg>;
    case 'ReturnWaybills':
      return <svg {...common}><path d="M3 9h9v7H3z" /><path d="M12 11h4l3 3v2h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="16.5" cy="18" r="1.6" /><path d="M10 4.5H5.5" /><path d="M7.5 2.3L5.3 4.5l2.2 2.2" /></svg>;
    // İade türevleri — sağlam: onay, hasarlı: uyarı, tedarikçi: bina.
    case 'ReturnInvoicesGood':
    case 'ReturnOrdersGood':
    case 'ReturnWaybillsGood':
      return <svg {...common}><path d="M4 8.2L12 4l8 4.2v7.6L12 20l-8-4.2z" /><path d="M8.5 12l2.4 2.4 4.6-4.6" /></svg>;
    case 'ReturnInvoicesDamaged':
    case 'ReturnOrdersDamaged':
    case 'ReturnWaybillsDamaged':
      return <svg {...common}><path d="M4 8.2L12 4l8 4.2v7.6L12 20l-8-4.2z" /><path d="M12 8.6v4" /><path d="M12 15.6h.01" /></svg>;
    case 'ReturnInvoicesVendor':
    case 'ReturnOrdersVendor':
    case 'ReturnWaybillsVendor':
      return <svg {...common}><path d="M3 21h8V8L7 5.5 3 8z" /><path d="M11 21h10v-9h-5" /><path d="M6 11h2" /><path d="M6 15h2" /><path d="M15 16h2" /></svg>;
    case 'VendorReturns':
      return <svg {...common}><path d="M13 21h8V6l-4-2.5L13 6z" /><path d="M16 10h2" /><path d="M16 14h2" /><path d="M9 12H3" /><path d="M6 9l-3 3 3 3" /></svg>;
    case 'ServiceInvoices':
      return <svg {...common}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9.5 12h5" /><path d="M9.5 15.5h3" /></svg>;
    case 'ServiceInvoicesGiven':
      return <svg {...common}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M12 16v-5" /><path d="M10 13l2-2 2 2" /></svg>;
    case 'ServiceInvoicesReceived':
      return <svg {...common}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M12 11v5" /><path d="M10 14l2 2 2-2" /></svg>;
    case 'GibMonitor':
      return <svg {...common}><path d="M12 3l7 3v5.5c0 4-3 7.4-7 8.5-4-1.1-7-4.5-7-8.5V6z" /><path d="M9 12l2 2 4-4" /></svg>;
    case 'ReturnableQuantities':
      return <svg {...common}><path d="M4 8.2L12 4l8 4.2v7.6L12 20l-8-4.2z" /><path d="M4 8.2L12 12.4l8-4.2" /><path d="M12 12.4V20" /></svg>;
    case 'DepositReturns':
      return <svg {...common}><path d="M9 3h6l-.6 3H9.6z" /><path d="M9.6 6L8 10v10h8V10l-1.6-4" /><path d="M8 14h8" /></svg>;
    case 'DepositReconciliation':
      return <svg {...common}><path d="M4 7h12" /><path d="M13 4l3 3-3 3" /><path d="M20 17H8" /><path d="M11 14l-3 3 3 3" /></svg>;
    case 'GrpDocumentAutomation':
      return <svg {...common}><path d="M6 3h7l4 4v6" /><path d="M13 3v4h4" /><path d="M6 3v18h5" /><circle cx="17.5" cy="17.5" r="2.5" /><path d="M17.5 13.6v1.2M17.5 20.2v1.2M13.6 17.5h1.2M20.2 17.5h1.2" /></svg>;
    case 'DocumentDerivations':
      return <svg {...common}><rect x="3" y="3" width="8" height="7" rx="1.5" /><rect x="14" y="8" width="7" height="6" rx="1.5" /><rect x="14" y="16" width="7" height="5" rx="1.5" /><path d="M7 10v7.5a1 1 0 0 0 1 1h6" /><path d="M7 10v1a1 1 0 0 0 1 1h6" /></svg>;
    case 'GrpConsignment':
    case 'ConsignmentWaybills':
      return <svg {...common}><path d="M4 8.2L12 4l8 4.2v7.6L12 20l-8-4.2z" /><path d="M4 8.2L12 12.4l8-4.2" /><path d="M12 12.4V20" /><path d="M8 6.1l8 4.2" /></svg>;
    case 'ConsignmentPurchaseWaybills':
      return <svg {...common}><path d="M4 9.2L12 5l8 4.2v7.6L12 21l-8-4.2z" /><path d="M4 9.2L12 13.4l8-4.2" /><path d="M12 13.4V21" /><path d="M12 1v3.2" /><path d="M10.3 2.9L12 4.6l1.7-1.7" /></svg>;
    case 'BelgeDizayni':
      return <svg {...common}><rect x="3" y="3" width="12" height="18" rx="2" /><path d="M6.5 7.5h5" /><path d="M6.5 11h5" /><path d="M14.5 20.5l6.5-6.5-2.5-2.5L12 18v2.5z" /></svg>;
    case 'DocumentSeries':
      return <svg {...common}><path d="M7 3h6l3 3v10H7z" /><path d="M13 3v3h3" /><path d="M17 8h3v13H10v-3" /><path d="M9.5 10h4" /></svg>;
    case 'EDocumentControl':
      return <svg {...common}><path d="M6 3h8l4 4v8H6z" /><path d="M14 3v4h4" /><path d="M4.5 18.5a3 3 0 0 1 3-3h9a3 3 0 0 1 0 6h-9a3 3 0 0 1-3-3z" /><path d="M9 18.5l1.6 1.6 3.4-3.4" /></svg>;
    case 'NamaDocuments':
      return <svg {...common}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><circle cx="11" cy="12.5" r="2.2" /><path d="M7.8 18.4a3.6 3.6 0 0 1 6.4 0" /></svg>;

    // ===== Finans: tahsilat / ödeme =====
    // Yön kuralı: tahsilat = kasaya GİREN ok, ödeme = kasadan ÇIKAN ok.
    case 'FinanceCollections':
      return <svg {...common}><rect x="3" y="8" width="18" height="12" rx="2.5" /><path d="M3 12h18" /><path d="M12 2v5" /><path d="M9.6 4.8L12 7.2l2.4-2.4" /></svg>;
    case 'FinancePayments':
      return <svg {...common}><rect x="3" y="8" width="18" height="12" rx="2.5" /><path d="M3 12h18" /><path d="M12 7V2" /><path d="M9.6 4.4L12 2l2.4 2.4" /></svg>;
    case 'CollectionCash':
      return <svg {...common}><rect x="2.5" y="6.5" width="19" height="11" rx="2" /><circle cx="12" cy="12" r="2.6" /><path d="M6 10v4M18 10v4" /></svg>;
    case 'PaymentCash':
      return <svg {...common}><rect x="2.5" y="8.5" width="19" height="11" rx="2" /><circle cx="12" cy="14" r="2.4" /><path d="M6 12v4M18 12v4" /><path d="M9.6 4.4L12 2l2.4 2.4" /><path d="M12 2v4" /></svg>;
    case 'CollectionCreditCard':
      return <svg {...common}><rect x="2.5" y="5.5" width="19" height="13" rx="2.5" /><path d="M2.5 10h19" /><path d="M6 14.5h3.5" /></svg>;
    case 'PaymentCreditCard':
      return <svg {...common}><rect x="2.5" y="7.5" width="19" height="13" rx="2.5" /><path d="M2.5 12h19" /><path d="M6 16.5h3.5" /><path d="M18.5 2v4" /><path d="M16.4 4l2.1 2 2.1-2" /></svg>;
    case 'CollectionCheque':
      return <svg {...common}><rect x="2.5" y="6" width="19" height="12" rx="2" /><path d="M6 10h6" /><path d="M6 13.5h3.5" /><path d="M14 14.5l1.8 1.8 3.4-3.6" /></svg>;
    case 'PaymentCheque':
      return <svg {...common}><rect x="2.5" y="6" width="19" height="12" rx="2" /><path d="M6 10h6" /><path d="M6 13.5h3.5" /><path d="M15 16.5v-4.6" /><path d="M13 13.8l2-2 2 2" /></svg>;
    case 'CollectionNote':
      return <svg {...common}><path d="M4 5.5h16v13H4z" /><path d="M4 5.5l8 5.5 8-5.5" /><path d="M8 16.5h3" /></svg>;
    case 'PaymentNote':
      return <svg {...common}><path d="M4 7.5h16v13H4z" /><path d="M4 7.5l8 5.5 8-5.5" /><path d="M12 5.5V1.5" /><path d="M9.9 3.4L12 1.4l2.1 2" /></svg>;
    case 'CollectionDbs':
      return <svg {...common}><path d="M3 9.5L12 4l9 5.5" /><path d="M5 9.5v9M19 9.5v9" /><path d="M9 12v4M15 12v4" /><path d="M3.5 21h17" /></svg>;
    case 'PaymentDbs':
      return <svg {...common}><path d="M3 11L12 5.5 21 11" /><path d="M5 11v8M19 11v8" /><path d="M9 13.5v3.5M15 13.5v3.5" /><path d="M3.5 21h17" /><path d="M12 5.5V1.8" /></svg>;
    case 'CollectionBankTransfer':
      return <svg {...common}><path d="M3 10L12 5l9 5" /><path d="M5 10v6M19 10v6" /><path d="M3.5 19h17" /><path d="M9 13h6" /><path d="M12.5 11.2L14.5 13l-2 1.8" /></svg>;
    case 'PaymentBankTransfer':
      return <svg {...common}><path d="M3 10L12 5l9 5" /><path d="M5 10v6M19 10v6" /><path d="M3.5 19h17" /><path d="M15 13H9" /><path d="M11.5 11.2L9.5 13l2 1.8" /></svg>;
    case 'CollectionCreditEntry':
      return <svg {...common}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M11.5 11.5v5M9 14h5" /></svg>;
    case 'PaymentDebitEntry':
      return <svg {...common}><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 14h5" /></svg>;
    case 'UnallocatedCollections':
      return <svg {...common}><circle cx="9" cy="12" r="6" /><path d="M9 9v6M7 12h4" /><path d="M17 8.5a2 2 0 1 1 2 2c-.6.5-1 .9-1 1.8" /><path d="M18 16h.01" /></svg>;
    // Ödeme Tipleri: kart + karşısındaki tanım satırları — "tanımlı ödeme yöntemleri".
    // Ödeme Takip'in düz kartından ayrışsın diye liste çizgileri eklendi.
    case 'PaymentTypes':
      return <svg {...common}><rect x="2.5" y="6" width="11" height="8" rx="1.8" /><path d="M2.5 9h11" /><path d="M5 12h2.5" /><path d="M17 8h4.5M17 12h4.5M17 16h4.5" /><path d="M14.4 8h.01M14.4 12h.01M14.4 16h.01" /></svg>;
    case 'InvoiceAging':
      return <svg {...common}><path d="M6 3h7l4 4v5" /><path d="M13 3v4h4" /><path d="M6 3v18h6" /><circle cx="17.5" cy="16.5" r="4.5" /><path d="M17.5 14.3v2.3l1.6 1" /></svg>;

    // ===== Stok hareketleri =====
    case 'StockEntry':
      return <svg {...common}><path d="M4 11.2L12 7l8 4.2v6.6L12 22l-8-4.2z" /><path d="M4 11.2L12 15.4l8-4.2" /><path d="M12 4.5V1" /><path d="M9.6 2.4L12 4.8l2.4-2.4" /></svg>;
    case 'StockExit':
      return <svg {...common}><path d="M4 11.2L12 7l8 4.2v6.6L12 22l-8-4.2z" /><path d="M4 11.2L12 15.4l8-4.2" /><path d="M12 1v3.8" /><path d="M9.6 3.2L12 .8l2.4 2.4" /></svg>;
    case 'StockTransfer':
      return <svg {...common}><rect x="2.5" y="4" width="7" height="7" rx="1.2" /><rect x="14.5" y="13" width="7" height="7" rx="1.2" /><path d="M12.5 7.5h6.5" /><path d="M16.8 5.4l2.2 2.1-2.2 2.1" /><path d="M11.5 16.5H5" /><path d="M7.2 14.4L5 16.5l2.2 2.1" /></svg>;
    case 'VehicleTransfers':
      return <svg {...common}><path d="M2 9h9v7H2z" /><path d="M11 11h4l3 3v2h-7z" /><circle cx="6" cy="18" r="1.6" /><circle cx="15.5" cy="18" r="1.6" /><path d="M14 5h7" /><path d="M18.4 2.8L20.8 5l-2.4 2.2" /></svg>;
    case 'TransitBalances':
      return <svg {...common}><path d="M2 12h9v6H2z" /><path d="M11 13.5h4l3 3v1.5h-7z" /><circle cx="6" cy="19.5" r="1.4" /><circle cx="15" cy="19.5" r="1.4" /><path d="M2.5 6h7" /><path d="M4.5 9h5" /><path d="M14 4.5h6" /></svg>;

    // ===== Ürün =====
    case 'ProductRouting':
      return <svg {...common}><rect x="2" y="9" width="6" height="6" rx="1.2" /><rect x="16" y="3" width="6" height="6" rx="1.2" /><rect x="16" y="15" width="6" height="6" rx="1.2" /><path d="M8 12h4v-6h4" /><path d="M12 12v6h4" /></svg>;
    case 'CriticalSku':
      return <svg {...common}><path d="M4 8.6L11 5l7 3.6v6.8L11 19l-7-3.6z" /><path d="M4 8.6L11 12.2l7-3.6" /><path d="M18.5 3v4" /><path d="M18.5 9.4h.01" /></svg>;
    case 'ProductSegmentationTree':
      return <svg {...common}><rect x="9" y="2.5" width="6" height="5" rx="1.2" /><rect x="2.5" y="16.5" width="6" height="5" rx="1.2" /><rect x="15.5" y="16.5" width="6" height="5" rx="1.2" /><path d="M12 7.5v4" /><path d="M5.5 16.5v-2.8h13v2.8" /></svg>;

    // ===== Ticari pazarlama =====
    case 'Targets':
      return <svg {...common}><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" /></svg>;
    case 'NonSaleReasons':
      return <svg {...common}><path d="M2.5 4h2.2l2.4 10.5h9.4" /><path d="M6.6 8h11.9l-1.2 5" /><circle cx="9" cy="19" r="1.5" /><circle cx="16.5" cy="19" r="1.5" /><path d="M11.5 8.5l4 4M15.5 8.5l-4 4" /></svg>;
    case 'CampaignAuthorizations':
      return <svg {...common}><path d="M3 10.5v3l3 .5 6 4V6l-6 4z" /><path d="M15.5 9.5a3.5 3.5 0 1 1 0 5" /><path d="M14 19.5l1.7 1.7 3.6-3.8" /></svg>;

    // ===== Demirbaş (soğutucu gövdesi ortak dil) =====
    case 'Assets':
      return <svg {...common}><rect x="5" y="2.5" width="14" height="19" rx="2.5" /><path d="M5 10h14" /><path d="M8.5 6h.01" /><path d="M8.5 13.5h.01" /></svg>;
    case 'AssetCounts':
      return <svg {...common}><rect x="3.5" y="2.5" width="11" height="19" rx="2.5" /><path d="M3.5 9.5h11" /><path d="M6.5 6h.01" /><path d="M16 15l1.7 1.7 3.4-3.6" /><path d="M16 8.5l1.7 1.7 3.4-3.6" /></svg>;
    case 'AssetCountOrders':
      return <svg {...common}><rect x="3" y="4" width="11" height="17" rx="2" /><path d="M6.5 3h4v3h-4z" /><path d="M6 11h5M6 15h5" /><rect x="16.5" y="9" width="5" height="9" rx="1.5" /><path d="M16.5 12.5h5" /></svg>;
    case 'AssetModels':
      return <svg {...common}><rect x="4" y="2.5" width="10" height="19" rx="2.5" /><path d="M4 9.5h10" /><path d="M7 6h.01" /><path d="M17 6.5h4M17 11h4M17 15.5h4" /></svg>;
    case 'AssetAttributeDefs':
      return <svg {...common}><rect x="3" y="2.5" width="10" height="19" rx="2.5" /><path d="M3 9.5h10" /><path d="M6 6h.01" /><path d="M20.5 8.2l-4.6 4.6a1.6 1.6 0 0 1-2.3 0" /><path d="M16.5 6.5h4.5V11" /><path d="M18.6 8.6h.01" /></svg>;
    case 'AssetDrafts':
      return <svg {...common}><rect x="3.5" y="2.5" width="11" height="19" rx="2.5" /><path d="M3.5 9.5h11" /><path d="M6.5 6h.01" /><path d="M15.5 17.8l4.7-4.7 2.1 2.1-4.7 4.7h-2.1z" /></svg>;
    case 'AssetImport':
      return <svg {...common}><rect x="3.5" y="2.5" width="11" height="19" rx="2.5" /><path d="M3.5 9.5h11" /><path d="M6.5 6h.01" /><path d="M19 6v7" /><path d="M16.6 10.6L19 13l2.4-2.4" /><path d="M16 16h6" /></svg>;

    // ===== Saha yönetimi =====
    case 'GrpFieldManagement':
      return <svg {...common}><path d="M2.5 6.5L8.5 4v13l-6 2.5z" /><path d="M8.5 4l6 2.2" /><circle cx="17.5" cy="9" r="2.6" /><path d="M12.8 20v-1.6a4.7 4.7 0 0 1 4.7-4.7 4.7 4.7 0 0 1 4.7 4.7V20" /></svg>;
    case 'SalesOrgHierarchy':
      return <svg {...common}><rect x="9" y="2.5" width="6" height="4.5" rx="1.2" /><rect x="2" y="17" width="6" height="4.5" rx="1.2" /><rect x="16" y="17" width="6" height="4.5" rx="1.2" /><path d="M12 7v3.5" /><path d="M5 17v-3.5h14V17" /></svg>;
    case 'Personnel':
      return <svg {...common}><rect x="2.5" y="4.5" width="19" height="15" rx="2.5" /><circle cx="8.5" cy="11" r="2.4" /><path d="M4.8 16.5a3.9 3.9 0 0 1 7.4 0" /><path d="M14.5 10h4.5M14.5 13.5h4.5" /></svg>;
    case 'RoutePlans':
      return <svg {...common}><path d="M2.5 6.5L8.5 4l7 2.5L21.5 4v13l-6 2.5-7-2.5-6 2.5z" /><path d="M8.5 4v13" /><path d="M15.5 6.5v13" /></svg>;
    case 'ShipmentPlanning':
      return <svg {...common}><rect x="2.5" y="4.5" width="11" height="10" rx="1.8" /><path d="M2.5 8h11" /><path d="M6 3v3M10 3v3" /><path d="M9 16.5h6l3 3v2.5h-9z" /><circle cx="11.5" cy="22" r="1.3" /><circle cx="17" cy="22" r="1.3" /><path d="M5.5 11.5h4" /></svg>;

    // ===== Sistem yönetimi =====
    case 'VisitActivityTypes':
      return <svg {...common}><rect x="4" y="3.5" width="13" height="17" rx="2" /><path d="M7.5 8.5l1.4 1.4 2.6-2.8" /><path d="M7.5 14.5l1.4 1.4 2.6-2.8" /><circle cx="18.5" cy="17" r="3.5" /><path d="M18.5 15.3v1.8l1.2.8" /></svg>;
    case 'ScheduledTasks':
      return <svg {...common}><circle cx="10" cy="12" r="7.5" /><path d="M10 7.8V12l2.8 1.8" /><path d="M18.5 3.5v1.6M18.5 8v1.6" /><path d="M20.4 6.5H22M15 6.5h1.6" /></svg>;
    case 'SupportTickets':
      return <svg {...common}><path d="M3 8.5V6.5a1.5 1.5 0 0 1 1.5-1.5h15A1.5 1.5 0 0 1 21 6.5v2a2 2 0 0 0 0 7v2a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-2a2 2 0 0 0 0-7z" /><path d="M12 8v1.5M12 11.2v1.5M12 14.5V16" /></svg>;
    case 'GeneralParameters':
      return <svg {...common}><path d="M4 7h9M17 7h3" /><path d="M4 12h3M11 12h9" /><path d="M4 17h11M19 17h1" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="12" r="2" /><circle cx="17" cy="17" r="2" /></svg>;
    case 'GrpUserPermission':
      return <svg {...common}><path d="M12 2.8l7 2.8v5.2c0 4.1-2.9 7.7-7 8.9-4.1-1.2-7-4.8-7-8.9V5.6z" /><circle cx="12" cy="10" r="2.2" /><path d="M8.6 15.6a3.9 3.9 0 0 1 6.8 0" /></svg>;
    case 'UserGroups':
      return <svg {...common}><circle cx="9" cy="8" r="3.2" /><path d="M3 19v-1.2A4.8 4.8 0 0 1 7.8 13h2.4a4.8 4.8 0 0 1 4.8 4.8V19" /><path d="M17 5.5a3 3 0 0 1 0 5.6" /><path d="M21 19v-1a3.6 3.6 0 0 0-2.6-3.4" /></svg>;
    case 'PermissionTemplates':
      return <svg {...common}><path d="M11 2.8l7 2.8v5.2c0 3.4-2 6.5-5 8" /><path d="M11 2.8L4 5.6v5.2c0 4.1 2.9 7.7 7 8.9" /><path d="M7.5 9h7M7.5 12.5h4.5" /></svg>;
    case 'FieldRights':
      return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /><path d="M10 9v11" /><rect x="13.5" y="13" width="5.5" height="4.5" rx="1" /><path d="M15 13v-1.5a1.8 1.8 0 0 1 3.5 0V13" /></svg>;
    case 'EffectivePermissions':
      return <svg {...common}><path d="M12 2.8l7 2.8v5.2c0 4.1-2.9 7.7-7 8.9-4.1-1.2-7-4.8-7-8.9V5.6z" /><path d="M8.8 11.6l2.2 2.2 4.2-4.4" /></svg>;
    case 'GrpSecurityMonitoring':
      return <svg {...common}><path d="M12 2.8l7 2.8v5.2c0 4.1-2.9 7.7-7 8.9-4.1-1.2-7-4.8-7-8.9V5.6z" /><path d="M6.5 12h2.8l1.6-3 2 6 1.4-3h2.8" /></svg>;
    case 'LoginAudit':
      return <svg {...common}><path d="M13 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h7" /><path d="M15.5 8.5L19 12l-3.5 3.5" /><path d="M19 12h-9" /></svg>;
    case 'Sessions':
      return <svg {...common}><rect x="2.5" y="4" width="19" height="12" rx="2" /><path d="M8 20h8" /><path d="M12 16v4" /><path d="M6 11l2.5-2.5L11 11l3-4 3.5 4.5" /></svg>;
    case 'Devices':
      return <svg {...common}><rect x="2.5" y="5" width="11" height="8" rx="1.5" /><path d="M1.5 16h13" /><rect x="16" y="7.5" width="6" height="12" rx="1.8" /><path d="M18.5 17.5h1" /></svg>;
    case 'AuditLog':
      return <svg {...common}><path d="M6 3h7l4 4v5.5" /><path d="M13 3v4h4" /><path d="M6 3v18h6.5" /><path d="M9 9h2M9 12.5h4M9 16h3" /><circle cx="17.5" cy="17.5" r="3.8" /><path d="M17.5 15.6v1.9l1.4.9" /></svg>;

    // ===== SuperAdmin =====
    case 'TenantManagement':
      return <svg {...common}><path d="M3 21h7V6.5L6.5 4 3 6.5z" /><path d="M10 21h11V10.5L15.5 8 10 10.5" /><path d="M5.5 10h2M5.5 14h2M13 14h2M17.5 14h1.5M13 17.5h2M17.5 17.5h1.5" /></svg>;
    case 'Tenants':
      return <svg {...common}><path d="M4 21V6.5L11 3l7 3.5V21z" /><path d="M8 10h2M12.5 10h2M8 14h2M12.5 14h2" /><path d="M9.5 21v-3.5h3V21" /></svg>;
    case 'TenantUsage':
      return <svg {...common}><path d="M3.5 21V7.5L10 4.5l6.5 3V21z" /><path d="M7 11h2M11 11h2M7 14.5h2M11 14.5h2" /><path d="M19.5 21v-6" /><path d="M16.5 21v-3" /></svg>;
    case 'Licenses':
      return <svg {...common}><rect x="3" y="4" width="18" height="13" rx="2" /><circle cx="9" cy="9.5" r="2.2" /><path d="M13.5 8.5h4M13.5 11.5h4" /><path d="M6.2 14.5a3.4 3.4 0 0 1 5.6 0" /><path d="M8 17v4l2-1.2 2 1.2v-4" /></svg>;
    case 'GrpDynamicMigration':
      return <svg {...common}><ellipse cx="7" cy="5.5" rx="4.5" ry="2.2" /><path d="M2.5 5.5v6c0 1.2 2 2.2 4.5 2.2s4.5-1 4.5-2.2v-6" /><path d="M15 9h6" /><path d="M18.4 6.4L21 9l-2.6 2.6" /><path d="M21 16h-6" /><path d="M17.6 13.4L15 16l2.6 2.6" /></svg>;
    case 'SoapServices':
      return <svg {...common}><path d="M6.5 17.5a4 4 0 0 1-.5-8 5.5 5.5 0 0 1 10.6-1.4 3.7 3.7 0 0 1 .4 7.4" /><path d="M10 13.5L8 15.5l2 2" /><path d="M14 13.5l2 2-2 2" /></svg>;
    default:
      return <span className="sidebar-icon-fallback">{getIconText({ title: title || name || '' })}</span>;
  }
}

function normalizeId(item) {
  return item.parentID ?? item.parentId ?? item.ParentID ?? item.ParentId ?? null;
}

function getIconText(item) {
  const title = item.title || item.name || '';
  return title
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();
}

function AIAssistantCard({ navigate }) {
  return (
    <button
      type="button"
      className="sidebar-ai-card"
      onClick={() => navigate('/assistant')}
      title="Rona AI"
      aria-label="Rona AI'yı aç"
    >
      <span className="sidebar-ai-card-icon" aria-hidden="true">
        <svg width={20} height={20} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
          <path d="M19 13l.75 2.25L22 16l-2.25.75L19 19l-.75-2.25L16 16l2.25-.75L19 13z" />
          <path d="M5 17l.5 1.5L7 19l-1.5.5L5 21l-.5-1.5L3 19l1.5-.5L5 17z" />
        </svg>
      </span>
      <span className="sidebar-ai-card-meta">
        <span className="sidebar-ai-card-title">Rona AI</span>
        <span className="sidebar-ai-card-sub">Sorularını sor · özetle</span>
      </span>
      <span className="sidebar-ai-card-arrow" aria-hidden="true">
        <svg width={14} height={14} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </span>
    </button>
  );
}

function NavMenuInner() {
  const { t } = useLocalization();
  const { menuItems, loading, hasLoaded, publishResolvedMenus } = useMenu();
  const { favoriteIds, toggle: toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState({});
  // Elle aç/kapa; seçim kalıcı (localStorage) — açık bıraktıysa açık, kapattıysa kapalı kalır.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
  });
  // #272 Oto Gizle: menü daraltılmış (dar) durur, üzerine gelince overlay olarak açılır,
  //      fare ayrılınca yeniden daralır. İçerik hiç kaymaz (dar genişlik hep rezerve edilir).
  const [autoHide, setAutoHide] = useState(() => {
    try { return localStorage.getItem('sidebarAutoHide') === '1'; } catch { return false; }
  });
  // Oto Gizle modunda fare menü üzerindeyken geçici olarak genişletir.
  const [hovering, setHovering] = useState(false);
  // Görsel daraltma durumu: Oto Gizle'de fareye göre, değilse elle seçime göre.
  const effectiveCollapsed = autoHide ? !hovering : sidebarCollapsed;
  const [activeFlyoutId, setActiveFlyoutId] = useState(null);
  // #273 Daraltılmış menüde flyout'u viewport'a göre konumla (nav scroll'u korunur).
  const [flyoutPos, setFlyoutPos] = useState(null);
  const [menuQuery, setMenuQuery] = useState('');
  // "Favorilerim" grubu açık/kapalı — kullanıcı tercihi kalıcı (varsayılan açık).
  const [favOpen, setFavOpen] = useState(() => {
    try { return localStorage.getItem('sidebarFavOpen') !== '0'; } catch { return true; }
  });
  const flyoutCloseTimer = useRef(null);

  // DB menüsü doluysa onu, boşsa hardcoded defaultMenus'u kullan. Bir menü öğesi DB'de
  // henüz yoksa (migration uygulanmadıysa) hardcoded olarak enjekte et — böylece görünür.
  const sourceMenus = useMemo(() => {
    // Menü henüz yüklenmediyse (login sonrası ilk çekim sürüyor) HİÇBİR ŞEY gösterme —
    // hardcoded "tüm menü" fallback'i yalnız yükleme bittiği halde DB menüsü boşsa devreye girer.
    // Böylece "önce tüm menüler, sonra role göre menü" flash'ı oluşmaz.
    if (menuItems.length === 0 && !hasLoaded) return [];
    // SuperAdmin yalnız "Tenant Yönetimi" (9900 grubu + alt öğeleri) menülerini görür.
    // SAVUNMACI: backend (henüz yeni koda geçmemiş olabilir) fazla menü dönse bile client
    // tarafında ID bazlı filtreliyoruz; ayrıca defaultMenus fallback'i ve extras enjeksiyonu
    // (Gamification/Finans/Stoklar/BelgeDizaynı/SeriYönetimi) ATLANIR — yoksa operasyonel
    // menüler SuperAdmin'e sızar.
    if (isSuperAdmin()) {
      const SA_MENU_IDS = new Set([9900, 9901, 9902, 9903]);
      // #707: Kullanıcı Yönetimi (9604) + kapsayıcı "Sistem Yönetimi" grubu (9818) SuperAdmin'e
      // de açık. Grubun diğer alt öğeleri (Rol/İş Akışı/ETL…) BİLEREK dahil edilmez — yalnız bu iki
      // ID geçer; böylece operasyonel menüler SuperAdmin'e sızmaz.
      // 9826: Kullanıcı Yönetimi artık "Kullanıcı & Yetki" alt grubunun altında; kapsayıcı grup
      // listeye girmezse 9604 sahipsiz kalır ve ağaçta hiç render edilmez.
      const SA_EXTRA_IDS = new Set([9818, 9826, 9604]);
      return menuItems.filter((m) => {
        const mid = m.id ?? m.Id;
        const pid = m.parentID ?? m.ParentID;
        return SA_MENU_IDS.has(mid) || SA_MENU_IDS.has(pid) || SA_EXTRA_IDS.has(mid);
      });
    }
    const base = menuItems.length > 0 ? menuItems : defaultMenus;
    const extras = [];
    const has = (name, path) => base.some((m) => (m.name ?? m.Name) === name || (m.path ?? m.Path) === path);
    // Yeri değişen menüler ad/path ile aranamaz (DB'de bayat ad/path duruyor olabilir) → Id ile.
    const hasId = (id) => base.some((m) => (m.id ?? m.Id) === id);
    const parentOf = (groupName, fallbackId) => {
      const grp = base.find((m) => (m.name ?? m.Name) === groupName);
      return grp ? (grp.id ?? grp.Id) : fallbackId;
    };
    // "Stoklar" — Stok Yönetimi (9807) altında.
    if (!has('Stocks', '/stocks')) {
      extras.push({ id: 9536, parentID: parentOf('GrpStock', 9807), name: 'Stocks', title: 'Stoklar', path: '/stocks', sortOrder: 0 });
    }
    // "Fiyatlar" — Ticari Pazarlama (9804) altında (menü DB'de yoksa enjekte et).
    if (!has('Prices', '/prices')) {
      extras.push({ id: 9115, parentID: parentOf('GrpTradeMarketing', 9804), name: 'Prices', title: 'Fiyatlar', path: '/prices', sortOrder: 5 });
    }
    // "Belge Dizaynı" — Belge Merkezi (9805) altında.
    if (!has('BelgeDizayni', '/belge-dizayni')) {
      extras.push({ id: 9540, parentID: parentOf('GrpSalesOps', 9805), name: 'BelgeDizayni', title: 'Belge Dizaynı', path: '/belge-dizayni', sortOrder: 9 });
    }
    // "Seri Yönetimi" — Belge Merkezi (9805) altında.
    if (!has('DocumentSeries', '/document-series')) {
      extras.push({ id: 9541, parentID: parentOf('GrpSalesOps', 9805), name: 'DocumentSeries', title: 'Seri Yönetimi', path: '/document-series', sortOrder: 10 });
    }
    // "E-Belge Kontrol" (Faz 6) — Belge Merkezi (9805) altında. Menü migration'ı (20260713151000)
    // henüz UYGULANMAMIŞSA ekran erişilemez kalmasın diye enjekte edilir (BelgeDizayni deseni).
    if (!has('EDocumentControl', '/e-belge-kontrol')) {
      extras.push({ id: 9550, parentID: parentOf('GrpSalesOps', 9805), name: 'EDocumentControl', title: 'E-Belge Kontrol', path: '/e-belge-kontrol', sortOrder: 11 });
    }
    // "Depozito İade İrsaliyesi" + "Depozito Mutabakatı" (Faz 5) — İade İşlemleri (9522) altında.
    // Menü migration'ı (20260713141000) uygulanmamışsa ekranlar erişilemez kalmasın diye enjekte edilir.
    if (!has('DepositReturns', '/deposit-returns')) {
      extras.push({ id: 9548, parentID: parentOf('ReturnTransactions', 9522), name: 'DepositReturns', title: 'Depozito İade İrsaliyesi', path: '/deposit-returns', sortOrder: 7 });
    }
    if (!has('DepositReconciliation', '/deposit-reconciliation')) {
      extras.push({ id: 9549, parentID: parentOf('ReturnTransactions', 9522), name: 'DepositReconciliation', title: 'Depozito Mutabakatı', path: '/deposit-reconciliation', sortOrder: 8 });
    }
    // Raporlar ağacı — yapının TEK kaynağı REPORT_MENU. DB'de eksik olan satırlar enjekte edilir;
    // DB'den ESKİ konumuyla gelenler ise aşağıda (reportShape) yeni ebeveyn/başlık/sıraya çekilir.
    // Böylece migration henüz uygulanmamış ortamda da menü yeni yapıda görünür.
    for (const item of REPORT_MENU) {
      if (!has(item.name)) extras.push(item);
    }
    // Gamification (Satış Maratonu) — yeni üst grup + yapraklar (migration uygulanmadıysa enjekte et).
    if (!has('GrpGamification')) {
      extras.push({ id: 9814, name: 'GrpGamification', title: 'Gamification', hasChild: true, sortOrder: 12 });
    }
    const gamificationParent = parentOf('GrpGamification', 9814);
    for (const item of gamificationMenuItems) {
      if (!has(item.name, item.path)) {
        extras.push({ ...item, parentID: gamificationParent });
      }
    }
    // Finans — Alınan Tahsilatlar / Ödemelerim ağacı (migration uygulanmadıysa enjekte et).
    // #827 ile yeri değişen iki kalem ad/path ile aranamaz (DB'de eskisi duruyor) → Id ile bakılır.
    const financeParent = parentOf('GrpFinance', 9806);
    for (const item of financeMenuItems) {
      if (!hasId(item.id)) {
        extras.push(item.parentID === 9806 ? { ...item, parentID: financeParent } : item);
      }
    }
    // #833-835 İade alt menüleri + #829/#830 Konsinye grubu — migration uygulanmamış ortamda da
    // ekranlar erişilebilir kalsın diye enjekte edilir (E-Belge Kontrol deseni).
    for (const item of defaultMenus) {
      const isNewSplitLeaf = item.id >= 9562 && item.id <= 9572;
      if (isNewSplitLeaf && !hasId(item.id)) extras.push(item);
    }
    const merged = extras.length ? [...base, ...extras] : base;
    // Rapor ağacını yeni yapıya hizala: DB'den eski ebeveyn/başlıkla gelen satırlar (migration
    // uygulanmamış ya da yeni yapıdan önce yetkilendirilmiş ortamlar) REPORT_MENU'ye çekilir.
    // Yalnız konum/başlık ezilir — satırın DB'den gelmiş olması (yani YETKİSİ) korunur.
    return merged.map((m) => {
      // Yeri değişen menüler (Id sabit, konum yeni) — DB'den bayat geldiyse buraya çekilir.
      // PascalCase ikizleri de EZİLİR: DB satırları Path/Title/Name ile gelir ve menuPath()
      // camelCase boşsa PascalCase'e düşer — yalnız `path`i null'lamak eski yolu bırakırdı.
      const moved = RELOCATED_MENUS.get(m.id ?? m.Id);
      const row = moved
        ? { ...m, ...moved, id: m.id ?? m.Id, Path: moved.path, Title: moved.title, Name: moved.name, HasChild: moved.hasChild }
        : m;
      const shape = reportShape.get(row.name ?? row.Name);
      if (!shape) return row;
      return { ...row, parentID: shape.parentID, title: shape.title, sortOrder: shape.sortOrder };
    });
  }, [menuItems, hasLoaded]);

  // Gerçekte çizilen menü ağacını yayınla — Topbar'daki ★ açık sayfayı bu listeye göre
  // bir menüye bağlar (DB'de olmayan, enjekte edilmiş menüler de favorilenebilsin diye).
  useEffect(() => {
    publishResolvedMenus(sourceMenus);
  }, [sourceMenus, publishResolvedMenus]);

  useEffect(() => {
    // effectiveCollapsed görsel daraltmayı (ve içerik dar rezervini) sürer; autoHide overlay
    // davranışını ayrı attribute ile açar (içerik marjı dar sabitlenir — bkz. tokens.css).
    document.documentElement.setAttribute('data-sidebar-collapsed', effectiveCollapsed ? 'true' : 'false');
    document.documentElement.setAttribute('data-sidebar-autohide', autoHide ? 'true' : 'false');
    if (!effectiveCollapsed) setActiveFlyoutId(null);
    try {
      localStorage.setItem('sidebarCollapsed', sidebarCollapsed ? '1' : '0');
      localStorage.setItem('sidebarAutoHide', autoHide ? '1' : '0');
    } catch { /* yoksay */ }
    return () => {
      document.documentElement.removeAttribute('data-sidebar-collapsed');
      document.documentElement.removeAttribute('data-sidebar-autohide');
    };
  }, [effectiveCollapsed, autoHide, sidebarCollapsed]);

  useEffect(() => () => {
    if (flyoutCloseTimer.current) window.clearTimeout(flyoutCloseTimer.current);
  }, []);

  const openFlyout = (id, anchorEl) => {
    if (flyoutCloseTimer.current) window.clearTimeout(flyoutCloseTimer.current);
    flyoutCloseTimer.current = null;
    // #273 Flyout position:fixed → nav scroll edilebilir kalır, flyout kırpılmaz.
    if (anchorEl && anchorEl.getBoundingClientRect) {
      const r = anchorEl.getBoundingClientRect();
      const top = Math.max(8, Math.min(r.top - 4, window.innerHeight - 160));
      setFlyoutPos({ top, left: r.right + 6 });
    }
    setActiveFlyoutId(id);
  };

  const closeFlyoutSoon = () => {
    if (flyoutCloseTimer.current) window.clearTimeout(flyoutCloseTimer.current);
    flyoutCloseTimer.current = window.setTimeout(() => {
      setActiveFlyoutId(null);
      flyoutCloseTimer.current = null;
    }, 260);
  };

  const menus = useMemo(() => {
    // Yükleme sürüyorsa (sourceMenus boş) hiçbir grup gösterme — Kural Merkezi dahil flash önlenir.
    if (sourceMenus.length === 0) return [];
    const isActiveItem = (item) => item.isActive !== false && item.IsActive !== false;
    const sortByOrder = (a, b) => (a.sortOrder ?? a.SortOrder ?? 0) - (b.sortOrder ?? b.SortOrder ?? 0);
    const getId = (item) => item.id ?? item.Id ?? null;

    const visibleMenus = sourceMenus.filter(item =>
      isActiveItem(item) &&
      !footerMenuNames.has(item.name) &&
      !removedMenuNames.has(item.name)
    );

    // parentId'nin doğrudan çocuklarını + özyinelemeli torunlarını ağaç olarak kur (3+ seviye).
    const buildChildren = (parentId) => visibleMenus
      .filter(item => normalizeId(item) === parentId)
      .sort(sortByOrder)
      .map(node => ({ ...node, children: buildChildren(getId(node)) }));

    const grouped = visibleMenus
      .filter(item => normalizeId(item) == null)
      .sort(sortByOrder)
      .map(parent => ({ ...parent, children: buildChildren(getId(parent)) }));

    // "Kural ve Aksiyon Merkezi"ni standart bir ana menü grubu olarak en sona ekle.
    return [...grouped, { ...ruleActionCenterGroup, children: [...ruleActionCenterGroup.children] }];
  }, [sourceMenus]);

  const getMenuLabel = (item) => {
    if (forcedMenuLabels.has(item.name)) return menuTitleFallbacks[item.name];
    const translated = t(item.name);
    return translated === item.name ? item.title : translated;
  };

  // #5 — Menü arama: grup başlığı veya herhangi bir alt öğe eşleşirse göster.
  const searching = menuQuery.trim().length > 0;
  const filteredMenus = useMemo(() => {
    const q = menuQuery.trim().toLocaleLowerCase('tr-TR');
    if (!q) return menus;
    const has = (item) => getMenuLabel(item).toLocaleLowerCase('tr-TR').includes(q);
    // Özyinelemeli: düğüm eşleşirse alt ağacıyla; değilse eşleşen torunlarıyla tut.
    const filterNode = (node) => {
      if (has(node)) return node;
      const children = (node.children || []).map(filterNode).filter(Boolean);
      return children.length ? { ...node, children } : null;
    };
    return menus.map(filterNode).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus, menuQuery]);

  // Daraltılmış menüde arama gizli olduğundan filtre uygulanmaz.
  const renderedMenus = effectiveCollapsed ? menus : filteredMenus;

  // ── Favorilerim ────────────────────────────────────────────────────────────
  // Kullanıcının Topbar'daki ★ ile sabitlediği sayfalar. Favori YOKSA grup hiç çizilmez
  // (boş başlık gösterilmez). Yetkisi kalkan bir menü sourceMenus'ta bulunmaz → sessizce düşer.
  const favoriteNodes = useMemo(() => {
    if (!favoriteIds.length || sourceMenus.length === 0) return [];
    const byId = new Map(sourceMenus.map((m) => [m.id ?? m.Id, m]));
    return favoriteIds
      .map((id) => byId.get(id))
      .filter((m) => m && !(m.hasChild ?? m.HasChild) && (m.path ?? m.Path ?? menuPathFallbacks[m.name ?? m.Name]));
  }, [favoriteIds, sourceMenus]);

  const showFavorites = favoriteNodes.length > 0 && !searching;

  const renderFavorites = () => {
    if (effectiveCollapsed) {
      // Daraltılmışken favoriler en üstte düz ikon listesi olarak durur (flyout gerekmez).
      return (
        <div className="sidebar-fav-collapsed">
          {favoriteNodes.map((item) => (
            <div className="sidebar-item" key={`fav-${item.id ?? item.Id}`}>{renderMenuLink(item)}</div>
          ))}
        </div>
      );
    }
    return (
      <div className="sidebar-item sidebar-fav">
        <button
          className={`sidebar-link sidebar-group${favOpen ? ' open' : ''}`}
          type="button"
          onClick={() => setFavOpen((o) => { const next = !o; try { localStorage.setItem('sidebarFavOpen', next ? '1' : '0'); } catch { /* yoksay */ } return next; })}
        >
          <span className="sidebar-icon" aria-hidden="true"><StarIcon filled /></span>
          <span className="sidebar-link-label">Favorilerim</span>
          <span className="sidebar-chevron" aria-hidden="true">v</span>
        </button>
        {favOpen && (
          <div className="sidebar-children">
            {favoriteNodes.map((item) => (
              <div className="sidebar-child sidebar-fav-child" key={`fav-${item.id ?? item.Id}`}>
                {renderMenuLink(item, true)}
                <button
                  type="button"
                  className="sidebar-fav-remove"
                  aria-label={`${getMenuLabel(item)} — favorilerden çıkar`}
                  title="Favorilerden çıkar"
                  onClick={() => toggleFavorite(item.id ?? item.Id)}
                >
                  <StarIcon filled />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Çıkış tek noktadan (utils/session) yürür — bkz. TopbarActions'taki aynı çağrı.
  // 'manual': teşhis günlüğünde kullanıcının kendi çıkışı ile sistemin attığı
  // çıkışlar ayrılsın diye; Login bu sebep için mesaj göstermez.
  const handleLogout = () => endSession('manual', { source: 'navmenu' });

  // #272 Elle aç/kapa — Oto Gizle'yi de kapatır (elle sabitleme otomatiği ezer).
  const toggleCollapse = () => {
    setAutoHide(false);
    setHovering(false);
    setSidebarCollapsed((c) => !c);
  };
  // #272 Oto Gizle aç/kapa — kapanınca menü sabit açık kalsın. Açarken fare zaten menü
  //      üzerinde (butona basıldı) olduğundan hovering=true; fare ayrılınca daralır.
  const toggleAutoHide = () => {
    setAutoHide((a) => {
      const next = !a;
      setSidebarCollapsed(false);
      setHovering(next);
      return next;
    });
  };

  const renderMenuLink = (item, child = false) => {
    const path = item.path || menuPathFallbacks[item.name] || '#';
    const disabled = path === '#';
    // Raporu henüz yazılmamış maddeler: Menu tablosunda Caption kolonu olmadığı için
    // etiket isim üzerinden eşlenir (bkz. menuCaptionFallbacks).
    const caption = item.caption || menuCaptionFallbacks[item.name ?? item.Name];
    const content = (
      <>
        <span className={`sidebar-icon${child ? ' sidebar-child-icon' : ''}`} aria-hidden="true">
          <SidebarIcon name={item.name} title={item.title} />
        </span>
        {caption ? (
          <span className="sidebar-link-text">
            <span className="sidebar-link-label">{getMenuLabel(item)}</span>
            <span className="sidebar-link-caption">{caption}</span>
          </span>
        ) : (
          <span className="sidebar-link-label">{getMenuLabel(item)}</span>
        )}
        {item.badge && <span className="sidebar-badge">{item.badge}</span>}
      </>
    );

    if (item.name === 'Logout') {
      return (
        <button className="sidebar-link footer-link" type="button" onClick={handleLogout}>
          {content}
        </button>
      );
    }

    return (
      <NavLink
        to={disabled ? '#' : path}
        className={({ isActive }) => `sidebar-link${child ? ' sidebar-child-link' : ''}${isActive && !disabled ? ' active' : ''}`}
        // Başlık her zaman verilir: menü açıkken de çok uzun rapor adları kırpılabiliyor,
        // fareyle üstüne gelince tamamı görünsün.
        title={getMenuLabel(item)}
        onClick={(e) => {
          if (disabled) e.preventDefault();
          if (child) setActiveFlyoutId(null);
        }}
      >
        {content}
      </NavLink>
    );
  };

  // Daraltılmış flyout için: ara menüleri atlayıp tüm yaprakları düzleştir.
  const collectLeaves = (node) => (node.children || []).flatMap(
    (child) => ((child.children && child.children.length) ? collectLeaves(child) : [child]),
  );

  // Menü düğümünü özyinelemeli çiz: yaprak → link, çocuğu olan → açılır grup (3+ seviye).
  const renderNode = (item, depth = 0) => {
    const children = item.children || [];
    const hasChildren = children.length > 0 || item.hasChild;
    const isTop = depth === 0;

    if (!hasChildren) {
      return (
        <div className={isTop ? 'sidebar-item' : 'sidebar-child'} key={item.id}>
          {renderMenuLink(item, !isTop)}
        </div>
      );
    }

    const isOpen = searching ? true : (openGroups[item.id] ?? false);
    const flyoutLeaves = isTop ? collectLeaves(item) : [];

    return (
      <div
        className={`sidebar-item${isTop && effectiveCollapsed && activeFlyoutId === item.id ? ' flyout-open' : ''}`}
        key={item.id}
        onMouseEnter={isTop ? (e) => { if (effectiveCollapsed && !autoHide && flyoutLeaves.length > 0) openFlyout(item.id, e.currentTarget); } : undefined}
        onMouseLeave={isTop ? () => { if (effectiveCollapsed && !autoHide) closeFlyoutSoon(); } : undefined}
      >
        <button
          className={`sidebar-link sidebar-group${isOpen ? ' open' : ''}${isTop ? '' : ' sidebar-subgroup'}`}
          type="button"
          title={effectiveCollapsed ? getMenuLabel(item) : undefined}
          onClick={(e) => {
            if (isTop && effectiveCollapsed && !autoHide) {
              if (activeFlyoutId === item.id) setActiveFlyoutId(null); else openFlyout(item.id, e.currentTarget.closest('.sidebar-item') || e.currentTarget);
              return;
            }
            setOpenGroups(current => ({ ...current, [item.id]: !isOpen }));
          }}
        >
          <span className={`sidebar-icon${isTop ? '' : ' sidebar-child-icon'}`} aria-hidden="true"><SidebarIcon name={item.name} title={item.title} /></span>
          <span className="sidebar-link-label">{getMenuLabel(item)}</span>
          <span className="sidebar-chevron" aria-hidden="true">v</span>
        </button>
        {isTop && effectiveCollapsed && !autoHide && activeFlyoutId === item.id && flyoutLeaves.length > 0 && (
          <div
            className="sidebar-flyout"
            role="menu"
            aria-label={getMenuLabel(item)}
            style={flyoutPos ? { position: 'fixed', top: flyoutPos.top, left: flyoutPos.left } : undefined}
            onMouseEnter={() => openFlyout(item.id)}
            onMouseLeave={closeFlyoutSoon}
          >
            <div className="sidebar-flyout-title">
              <span className="sidebar-icon sidebar-flyout-title-icon" aria-hidden="true">
                <SidebarIcon name={item.name} title={item.title} />
              </span>
              <span>{getMenuLabel(item)}</span>
            </div>
            <div className="sidebar-flyout-list">
              {flyoutLeaves.map(child => (
                <div className="sidebar-flyout-item" key={child.id}>{renderMenuLink(child, true)}</div>
              ))}
            </div>
          </div>
        )}
        {!effectiveCollapsed && isOpen && (
          <div className="sidebar-children">
            {children.length > 0
              ? children.map(child => renderNode(child, depth + 1))
              : (
                <div className="sidebar-child" style={{ padding: '6px 14px', fontSize: 12, fontStyle: 'italic', opacity: 0.6 }}>
                  Henüz öğe yok
                </div>
              )}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`sidebar-shell${effectiveCollapsed ? ' sidebar-collapsed' : ''}${autoHide ? ' sidebar-autohide' : ''}`}
      onMouseEnter={autoHide ? () => setHovering(true) : undefined}
      onMouseLeave={autoHide ? () => setHovering(false) : undefined}
    >
      {/* #272 Oto Gizle aç/kapa — daraltılmış görünümde gizli (yalnız açık başlıkta çıkar). */}
      {!effectiveCollapsed && (
        <button
          className={`sidebar-autohide-toggle${autoHide ? ' active' : ''}`}
          type="button"
          onClick={toggleAutoHide}
          aria-pressed={autoHide}
          aria-label={autoHide ? 'Oto Gizle kapalı' : 'Oto Gizle aç'}
          title={autoHide ? 'Oto Gizle açık — menü sabitle' : 'Oto Gizle — menü otomatik daralsın'}
        >
          <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 17v5" />
            <path d="M9 10.76V6a3 3 0 0 1 3-3 3 3 0 0 1 3 3v4.76a2 2 0 0 0 .59 1.42L17 13.5V17H7v-3.5l1.41-1.32A2 2 0 0 0 9 10.76z" />
          </svg>
        </button>
      )}
      <button
        className="sidebar-collapse-toggle"
        type="button"
        onClick={toggleCollapse}
        aria-label={effectiveCollapsed ? 'Sol menüyü aç' : 'Sol menüyü kapat'}
        title={effectiveCollapsed ? 'Menüyü aç' : 'Menüyü kapat'}
      >
        <span aria-hidden="true">{effectiveCollapsed ? '›' : '‹'}</span>
      </button>

      <Link className="sidebar-brand" to="/dashboards/general" aria-label="SalesOps">
        <img className="sidebar-brand-mark" src={collapsedLogoPath} alt="" aria-hidden="true" />
        <img className="sidebar-brand-wordmark" src={expandedLogoPath} alt="SalesOps" />
      </Link>

      {!effectiveCollapsed && (
        <div className="sidebar-menu-search">
          <svg width={15} height={15} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={menuQuery}
            onChange={(e) => setMenuQuery(e.target.value)}
            placeholder="Menüde ara…"
            aria-label="Menüde ara"
          />
          {menuQuery && (
            <button type="button" className="sidebar-menu-search-clear" onClick={() => setMenuQuery('')} aria-label="Aramayı temizle">×</button>
          )}
        </div>
      )}

      <nav className="sidebar-nav" aria-label="Main menu">
        {loading && <div className="sidebar-loading">Loading menu...</div>}
        {!loading && searching && renderedMenus.length === 0 && (
          <div className="sidebar-loading">Sonuç yok</div>
        )}
        {showFavorites && renderFavorites()}
        {renderedMenus.map(item => renderNode(item, 0))}
      </nav>

      {footerMenus.length > 0 && (
        <div className="sidebar-footer">
          {footerMenus.map(item => (
            <div className="sidebar-footer-item" key={item.id}>{renderMenuLink(item)}</div>
          ))}
        </div>
      )}

      <AIAssistantCard navigate={navigate} />
    </aside>
  );
}

export class NavMenu extends Component {
  static displayName = NavMenu.name;

  render() {
    return <NavMenuInner />;
  }
}
